// @ts-nocheck
import { readReportRows } from './reportRows';
import { applyReportAmountBounds, validateReportAmountBounds, ReportAmountBounds } from './reportAmountBounds';
import { supabase } from './supabase';
import { Database, Tables, TablesInsert, TablesUpdate } from './database.types';
import { getCurrentUserContext, applySecurityFilter, validateUserAction, buildRoleBasedFilter } from './security';
import { UserContext } from './types';
import { resolveDocumentUrl } from './documentFiles';
import { createDetailedVoucher, detailedVoucherRequestId, detailedVoucherTenants, readDetailedVoucher, probeDetailedVoucher, updateDetailedVoucher, transitionDetailedVoucher,
  readLegacyVoucherDraft, updateLegacyVoucherDraft, type LegacyVoucherDraftInput, type DetailedVoucherInput } from './detailedVouchers';

// Type helpers
type DatabaseError = {
  message: string;
  details?: string;
  hint?: string;
};

// Timeout wrapper for database queries
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = 10000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error('Database query timeout')), timeoutMs)
    )
  ]);
};

// Retry wrapper for failed queries
const withRetry = async <T>(
  operation: () => Promise<T>, 
  maxRetries: number = 2,
  delayMs: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }
  
  throw lastError!;
};

type ApiResponse<T> = {
  data: T | null;
  error: DatabaseError | null;
  count?: number;
};

// Legacy response type for backward compatibility
type LegacyApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  count?: number;
};

// Role-based access guard
async function checkRoleAccess(allowedRoles: string[]): Promise<boolean> {
  try {
    const userContext = await getCurrentUserContext();
    if (!userContext) {
      return false;
    }
    return allowedRoles.includes(userContext.role);
  } catch (error) {
    console.error('[API] Error checking role access:', error);
    return false;
  }
}

// Generic functions
async function handleApiCall<T>(
  operation: () => Promise<{ data: T | null; error: any; count?: number }>
): Promise<ApiResponse<T>> {
  try {
    const result = await withTimeout(operation(), 10000); // 10 second timeout
    
    // Handle authentication errors specifically
    if (result.error) {
      const errorMessage = result.error.message || 'Unknown error';
      
      // Check for authentication-related errors
      if (errorMessage.includes('JWT expired') || 
          errorMessage.includes('Invalid Refresh Token') ||
          errorMessage.includes('refresh_token_not_found') ||
          errorMessage.includes('invalid_grant')) {
        
        // Import clearSession dynamically to avoid circular dependency
        const { clearSession } = await import('./supabase');
        await clearSession();
        
        return {
          data: null,
          error: { 
            message: 'Session expired. Please sign in again.', 
            details: 'AUTH_ERROR',
            hint: 'Redirect to login' 
          }
        };
      }
      
      return {
        data: result.data,
        error: { 
          message: errorMessage, 
          details: result.error.details, 
          hint: result.error.hint 
        },
        count: result.count
      };
    }
    
    return {
      data: result.data,
      error: null,
      count: result.count
    };
  } catch (error: any) {
    console.error('[API] Unexpected error:', error);
    
    // Check if it's a network error
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        data: null,
        error: { 
          message: 'Network error. Please check your connection and try again.',
          details: 'NETWORK_ERROR'
        }
      };
    }
    
    // Handle timeout errors specifically
    if (error.message === 'Database query timeout') {
      return {
        data: null,
        error: { 
          message: 'Request timeout. The server is taking too long to respond.', 
          details: 'TIMEOUT_ERROR',
          hint: 'Please check your internet connection and try again' 
        }
      };
    }
    
    return {
      data: null,
      error: { message: error.message || 'An unexpected error occurred' }
    };
  }
}

// Properties API
export const propertiesApi = {
  // Get all properties with optional filters and role-based access control
  async getAll(filters?: {
    owner_id?: string;
    status?: string;
    property_type?: string;
    city?: string;
    group_id?: string; // new: filter by building/group
  }): Promise<ApiResponse<Tables<'properties'>[]>> {
    // **SECURITY**: Get current user context for role-based filtering (cached)
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access properties' }
      };
    }

    // **SECURITY**: Check role access - buyers should not see properties list
    // Note: Tenants CAN see properties they are renting (filtered below)
    const hasAccess = ['admin', 'manager', 'owner', 'tenant'].includes(userContext.role);
    if (!hasAccess) {
      return {
        data: null,
        error: { message: 'Access denied: Insufficient permissions to view properties' }
      };
    }

    let query = supabase
      .from('properties')
      .select(`
        *,
        owner:profiles!properties_owner_id_fkey(id, first_name, last_name, email, phone_number),
        group:property_groups!properties_group_id_fkey(id, name, group_type)
      `)
      .eq('is_sub_property', false) // Filter out sub-properties from main list
      .order('created_at', { ascending: false });

    // **SECURITY**: Apply role-based filtering BEFORE other filters
    if (userContext.role === 'owner') {
      // Owners see only their properties
      query = query.eq('owner_id', userContext.userId);
    } else if (userContext.role === 'tenant') {
      // Tenants see ONLY their rented properties (no available properties)
      const rentedPropertyIds = userContext.rentedPropertyIds || [];
      
      if (rentedPropertyIds.length > 0) {
        query = query.in('id', rentedPropertyIds);
      } else {
        // If tenant has no properties, return empty result immediately
        return {
          data: [],
          error: null,
          count: 0
        };
      }
    }

    // Apply additional filters
    if (filters?.owner_id) query = query.eq('owner_id', filters.owner_id);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.property_type) query = query.eq('property_type', filters.property_type);
    if (filters?.city) query = query.ilike('city', `%${filters.city}%`);
    if (filters?.group_id) query = query.eq('group_id', filters.group_id);
    return handleApiCall(() => query);
  },

  // Get property by ID with role-based access control
  async getById(id: string): Promise<ApiResponse<Tables<'properties'>>> {
    // **SECURITY**: Get current user context for access validation (cached)
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access property details' }
      };
    }

    // **SECURITY**: Check role access - buyers should not see properties
    // Note: Tenants CAN see properties they are renting
    const hasAccess = ['admin', 'manager', 'owner', 'tenant'].includes(userContext.role);
    if (!hasAccess) {
      return {
        data: null,
        error: { message: 'Access denied: Insufficient permissions to view property details' }
      };
    }

    // **SECURITY**: Check if user has access to this specific property
    const hasPropertyAccess = userContext.role === 'admin' ||
                     userContext.role === 'manager' ||
                     (userContext.role === 'owner' && userContext.ownedPropertyIds?.includes(id)) ||
                     (userContext.role === 'tenant' && userContext.rentedPropertyIds?.includes(id));

    if (!hasPropertyAccess) {
      return {
        data: null,
        error: { message: 'Access denied: You do not have permission to view this property' }
      };
    }
    
    return handleApiCall(() => 
      supabase
        .from('properties')
        .select(`
          *,
          owner:profiles!properties_owner_id_fkey(id, first_name, last_name, email, phone_number),
          contracts(*, tenant:profiles!contracts_tenant_id_fkey(id, first_name, last_name, email, phone_number)),
          maintenance_requests(*, tenant:profiles!maintenance_requests_tenant_id_fkey(id, first_name, last_name))
        `)
        .eq('id', id)
        .eq('is_sub_property', false)
        .single()
    );
  },

  // Helper function to check if property is available
  async isPropertyAvailable(id: string): Promise<boolean> {
    const { data } = await supabase
      .from('properties')
      .select('status')
      .eq('id', id)
      .single();
    
    return data?.status === 'available';
  },

  // Create property
  async create(property: TablesInsert<'properties'>, createdByUserId?: string): Promise<ApiResponse<Tables<'properties'>>> {
    // **SECURITY**: Get current user context for role validation
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to create property' }
      };
    }

    // **SECURITY**: Only admins and managers can create properties
    if (userContext.role !== 'admin' && userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Access denied: Only admins and managers can create properties' }
      };
    }

    return handleApiCall(async () => {
      // Insert the property
      const { data: newProperty, error } = await supabase
        .from('properties')
        .insert(property)
        .select()
        .single();

      if (error) throw error;

      // Send notification if property was created by someone other than the owner
      if (createdByUserId && property.owner_id && createdByUserId !== property.owner_id) {
        try {
          await notificationsApi.create({
            recipient_id: property.owner_id,
            sender_id: createdByUserId,
            notification_type: 'property_created',
            title: 'تم إضافة عقار جديد',
            message: `تم إضافة عقار جديد "${property.title}" إلى محفظتك بواسطة مدير العقار`,
            priority: 'medium',
            related_entity_type: 'property',
            related_entity_id: newProperty.id
          });        } catch (notificationError) {
          console.warn('Failed to send property creation notification:', notificationError);
          // Don't fail the property creation if notification fails
        }
      }

      return { data: newProperty, error: null };
    });
  },

  // Update property
  async update(id: string, updates: TablesUpdate<'properties'>): Promise<ApiResponse<Tables<'properties'>>> {
    // **SECURITY**: Get current user context for access validation
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to update property' }
      };
    }

    // **SECURITY**: Only admins and managers can update properties
    const hasAccess = userContext.role === 'admin' || userContext.role === 'manager';

    if (!hasAccess) {
      return {
        data: null,
        error: { message: 'Access denied: You do not have permission to update this property' }
      };
    }

    // **SECURITY**: First verify the property exists
    const { data: existingProperty, error: fetchError } = await supabase
      .from('properties')
      .select('id, owner_id')
      .eq('id', id)
      .single();

    if (fetchError || !existingProperty) {
      return {
        data: null,
        error: { message: 'Property not found' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('properties')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select(`
          *,
          owner:profiles!properties_owner_id_fkey(id, first_name, last_name, email, phone_number)
        `)
        .single()
    );
  },

  // Delete property (hard delete) - Admin/Manager only
  // Blocks if property has active contracts
  async delete(id: string): Promise<ApiResponse<null>> {
    // **SECURITY**: Get current user context for role validation
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to delete property' }
      };
    }

    // **SECURITY**: Only admins and managers can delete properties
    if (userContext.role !== 'admin' && userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Access denied: Only admins and managers can delete properties' }
      };
    }

    return handleApiCall(async () => {
      // Fail closed when the contract preflight cannot establish deletion is safe.
      const { data: activeContracts, error: contractsError } = await supabase
        .from('contracts')
        .select('id')
        .eq('property_id', id)
        .eq('status', 'active')
        .limit(1);

      if (contractsError) throw contractsError;
      if (!activeContracts) throw new Error('Cannot verify active contracts for this property');
      if (activeContracts.length > 0) {
        return {
          data: null,
          error: { message: 'Cannot delete property: Property has active contracts. Please end or terminate the contracts first.' }
        };
      }

      return supabase
        .from('properties')
        .delete()
        .eq('id', id);
    });
  },

  // Get properties for dashboard summary with role-based filtering
  async getDashboardSummary(): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      let invalidated = false;
      let actorId: string | null = null;
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT' || (actorId && session?.user.id !== actorId)) invalidated = true;
      });
      try {
        const initialSession = await withTimeout(supabase.auth.getSession());
        actorId = initialSession.data?.session?.user.id || null;
        const userContext = await getCurrentUserContext();
        const assertCurrent = () => {
          if (invalidated) throw new Error('Dashboard session changed');
        };
        assertCurrent();
        if (initialSession.error || !actorId || !userContext?.isAuthenticated ||
            userContext.userId !== actorId ||
            !['admin', 'manager', 'accountant', 'owner', 'tenant'].includes(userContext.role)) {
          throw new Error('Authentication required to access dashboard data');
        }

        const read = async (build: () => any) => {
          const result = await readReportRows(async (from, to) => {
            assertCurrent();
            const page = await withTimeout(build().order('id', { ascending: true }).range(from, to));
            assertCurrent();
            return page;
          });
          return result.data;
        };
        const rentedIds = userContext.rentedPropertyIds || [];
        const properties = userContext.role === 'tenant' && rentedIds.length === 0 ? [] : await read(() => {
          let query = supabase.from('properties').select('id,status').neq('status', 'deleted');
          if (userContext.role === 'owner') query = query.eq('owner_id', actorId);
          if (userContext.role === 'tenant') query = query.in('id', rentedIds);
          return query;
        });
        const contracts: any[] = [];
        const contractQuery = () => supabase.from('contracts')
          .select('id,status,rent_amount,property_id,tenant_id').eq('status', 'active');
        if (userContext.role === 'owner') {
          // Use the fully paginated owned-property result, not a potentially capped context ID list.
          const propertyIds = [...new Set(properties.map(property => property.id))];
          for (let offset = 0; offset < propertyIds.length; offset += 200) {
            contracts.push(...await read(() => contractQuery().in('property_id', propertyIds.slice(offset, offset + 200))));
          }
        } else if (userContext.role !== 'tenant' || rentedIds.length > 0) {
          contracts.push(...await read(() => userContext.role === 'tenant'
            ? contractQuery().eq('tenant_id', actorId) : contractQuery()));
        }

        let rentMinor = BigInt(0);
        const tenants = new Set<string>();
        for (const contract of contracts) {
          if (contract.tenant_id != null) {
            if (typeof contract.tenant_id !== 'string' || !contract.tenant_id.trim()) throw new Error('Dashboard contract tenant is invalid');
            tenants.add(contract.tenant_id);
          }
          const amount = String(contract.rent_amount ?? '');
          if (!/^\d+(\.\d{1,2})?$/.test(amount)) throw new Error('Dashboard contract rent is unavailable');
          const [whole, fraction = ''] = amount.split('.');
          rentMinor += BigInt(whole) * BigInt(100) + BigInt(fraction.padEnd(2, '0'));
        }
        // Sum in integer cents; reject totals too large to preserve cents in the numeric UI contract.
        if (rentMinor > BigInt('900719925474099')) throw new Error('Dashboard contract rent exceeds supported precision');
        const latestContext = await getCurrentUserContext();
        const latestSession = await withTimeout(supabase.auth.getSession());
        assertCurrent();
        if (!latestContext?.isAuthenticated || latestContext.userId !== actorId ||
            latestContext.role !== userContext.role || latestSession.error ||
            latestSession.data?.session?.user.id !== actorId) throw new Error('Dashboard access changed');
        return { data: {
          total_properties: properties.length,
          available: properties.filter(property => property.status === 'available').length,
          occupied: properties.filter(property => property.status === 'rented').length,
          maintenance: properties.filter(property => property.status === 'maintenance').length,
          total_monthly_rent: Number(rentMinor) / 100,
          active_contracts: contracts.length,
          active_contract_tenants: tenants.size,
        }, error: null };
      } finally {
        subscription.unsubscribe();
      }
    });
  },

  // Get properties available for assignment (unassigned properties)
  async getAvailableForAssignment(): Promise<ApiResponse<Tables<'properties'>[]>> {
    // **SECURITY**: Get current user context for role validation
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access properties' }
      };
    }

    // **SECURITY**: Only admins and managers can assign properties
    if (userContext.role !== 'admin' && userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Access denied: Only admins and managers can assign properties' }
      };
    }

    // Get all properties (unassigned + all organization properties)
    return handleApiCall(() =>
      supabase
        .from('properties')
        .select(`
          id,
          title,
          address,
          city,
          property_type,
          status,
          owner_id,
          owner:profiles!properties_owner_id_fkey(id, first_name, last_name)
        `)
        .eq('is_sub_property', false)
        .order('title', { ascending: true })
    );
  },

  // Assign multiple properties to an owner
  async assignToOwner(propertyIds: string[], ownerId: string): Promise<ApiResponse<{ success: number; failed: number }>> {
    // **SECURITY**: Get current user context for role validation
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to assign properties' }
      };
    }

    // **SECURITY**: Only admins and managers can assign properties
    if (userContext.role !== 'admin' && userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Access denied: Only admins and managers can assign properties to owners' }
      };
    }

    if (!propertyIds || propertyIds.length === 0) {
      return {
        data: { success: 0, failed: 0 },
        error: null
      };
    }

    // Validate that the owner exists and has the 'owner' role
    const { data: ownerProfile, error: ownerError } = await supabase
      .from('profiles')
      .select('id, role')
      .eq('id', ownerId)
      .single();

    if (ownerError || !ownerProfile) {
      return {
        data: null,
        error: { message: 'Owner not found' }
      };
    }

    if (ownerProfile.role !== 'owner') {
      return {
        data: null,
        error: { message: 'The specified user is not an owner' }
      };
    }

    // Update all properties with the new owner_id
    const { data, error } = await supabase
      .from('properties')
      .update({ owner_id: ownerId, updated_at: new Date().toISOString() })
      .in('id', propertyIds)
      .select('id');

    if (error) {
      return {
        data: null,
        error: { message: error.message }
      };
    }

    const successCount = data?.length || 0;
    const failedCount = propertyIds.length - successCount;

    return {
      data: { success: successCount, failed: failedCount },
      error: null
    };
  },

  // Remove a property from its current owner (set owner_id to null)
  async removeFromOwner(propertyId: string): Promise<ApiResponse<null>> {
    // **SECURITY**: Get current user context for role validation
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to remove property from owner' }
      };
    }

    // **SECURITY**: Only admins and managers can remove properties from owners
    if (userContext.role !== 'admin' && userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Access denied: Only admins and managers can remove properties from owners' }
      };
    }

    // Update the property to remove owner_id
    const { error } = await supabase
      .from('properties')
      .update({ owner_id: null, updated_at: new Date().toISOString() })
      .eq('id', propertyId);

    if (error) {
      return {
        data: null,
        error: { message: error.message }
      };
    }

    return {
      data: null,
      error: null
    };
  }
};

// Property Groups (Buildings/Compounds) API
export const propertyGroupsApi = {
  async getAll(filters?: {
    owner_id?: string;
    city?: string;
    status?: string;
  }): Promise<ApiResponse<Tables<'property_groups'>[]>> {
    let query = supabase
      .from('property_groups')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.owner_id) query = query.eq('owner_id', filters.owner_id);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.city) query = query.ilike('city', `%${filters.city}%`);

    return handleApiCall(() => query);
  },

  async getById(id: string): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      const [groupRes, unitsRes] = await Promise.all([
        supabase.from('property_groups').select('*').eq('id', id).single(),
        supabase.from('properties').select('*').eq('group_id', id)
      ]);

      if (groupRes.error) throw groupRes.error;
      if (unitsRes.error) throw unitsRes.error;

      return { data: { ...groupRes.data, units: unitsRes.data || [] }, error: null };
    });
  },

  async create(payload: TablesInsert<'property_groups'>): Promise<ApiResponse<Tables<'property_groups'>>> {
    return handleApiCall(() =>
      supabase.from('property_groups').insert(payload).select().single()
    );
  },

  async update(id: string, payload: TablesUpdate<'property_groups'>): Promise<ApiResponse<Tables<'property_groups'>>> {
    return handleApiCall(() =>
      supabase
        .from('property_groups')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
    );
  },

  async delete(id: string): Promise<ApiResponse<null>> {
    return handleApiCall(() => supabase.from('property_groups').delete().eq('id', id));
  },

  // Bulk-create apartment units under a group
  async createUnitsBulk(groupId: string, units: Array<Omit<TablesInsert<'properties'>, 'group_id'>>): Promise<ApiResponse<Tables<'properties'>[]>> {
    const rows = units.map(u => ({ ...u, group_id: groupId }));
    return handleApiCall(() =>
      supabase
        .from('properties')
        .insert(rows)
        .select('*')
    );
  }
};

// Profiles API (Tenants, Owners, etc.)
export const profilesApi = {
  // Get all profiles with filtering
  async getAll(filters?: {
    role?: string;
    profile_type?: string;
    status?: string;
  }): Promise<ApiResponse<Tables<'profiles'>[]>> {
    let query = supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.role) query = query.eq('role', filters.role);
    if (filters?.profile_type) query = query.eq('profile_type', filters.profile_type);
    if (filters?.status) query = query.eq('status', filters.status);

    return handleApiCall(() => query);
  },

  // Get profile by ID
  async getById(id: string): Promise<ApiResponse<Tables<'profiles'>>> {
    // Use simple query without foreign key relations to avoid errors
    // when relations don't exist for a profile (e.g., tenant without contracts)
    return handleApiCall(() =>
      supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single()
    );
  },

  // Create profile
  async create(profile: TablesInsert<'profiles'>): Promise<ApiResponse<Tables<'profiles'>>> {
    return handleApiCall(() => 
      supabase
        .from('profiles')
        .insert(profile)
        .select()
        .single()
    );
  },

  // Get current user profile
  async getCurrentUser(): Promise<ApiResponse<Tables<'profiles'>>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access profile' }
      };
    }

    return handleApiCall(() =>
      supabase
        .from('profiles')
        .select('*')
        .eq('id', userContext.userId)
        .single()
    );
  },

  // Update profile with role-based access control
  async update(id: string, updates: TablesUpdate<'profiles'>): Promise<ApiResponse<Tables<'profiles'>>> {
    // **SECURITY**: Get current user context for access validation
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to update profile' }
      };
    }

    // **SECURITY**: Check if user has permission to update this profile
    // Admin and Manager can update any profile
    // Users can only update their own profile
    const hasAccess = userContext.role === 'admin' ||
                     userContext.role === 'manager' ||
                     userContext.userId === id;

    if (!hasAccess) {
      return {
        data: null,
        error: { message: 'Access denied: You do not have permission to update this profile' }
      };
    }

    // **SECURITY**: First verify the profile exists
    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('id, role')
      .eq('id', id)
      .single();

    if (fetchError || !existingProfile) {
      return {
        data: null,
        error: { message: 'Profile not found' }
      };
    }

    return handleApiCall(() =>
      supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
    );
  },

  // Get tenants with their contracts - role-based access control
  async getTenants(): Promise<ApiResponse<any[]>> {
    // **SECURITY**: Get current user context for role-based filtering
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access tenant data' }
      };
    }

    return handleApiCall(async () => {
      let query = supabase
        .from('profiles')
        .select(`
          *,
          contracts:contracts!contracts_tenant_id_fkey(
            *,
            property:properties(title, address, city)
          )
        `)
        .in('role', ['tenant'])
        // Accept active or approved states; some seeds use approval_status
        .or('status.eq.active,status.eq.approved,approval_status.eq.approved');

      // **SECURITY**: Apply role-based filtering
      if (userContext.role === 'owner') {
        // Owners see only tenants of their properties
        const ownedPropertyIds = userContext.ownedPropertyIds || [];
        if (ownedPropertyIds.length > 0) {
          // Get tenant IDs who have contracts with owner's properties
          const { data: contracts } = await supabase
            .from('contracts')
            .select('tenant_id')
            .in('property_id', ownedPropertyIds);

          const tenantIds = contracts?.map(c => c.tenant_id) || [];
          if (tenantIds.length > 0) {
            query = query.in('id', tenantIds);
          } else {
            // Owner has no tenants
            return { data: [], error: null };
          }
        } else {
          // Owner has no properties
          return { data: [], error: null };
        }
      } else if (userContext.role === 'tenant') {
        // Tenants see only their own profile
        query = query.eq('id', userContext.userId);
      }
      // Admins and managers see all tenants (no additional filtering)

      return await query;
    });
  },

  // Get owners (profiles with role = owner)
  async getOwners(): Promise<ApiResponse<any[]>> {
    return handleApiCall(() =>
      supabase
        .from('profiles')
        .select('*')
        .eq('role', 'owner')
        .order('created_at', { ascending: false })
    );
  },

  // Delete tenant (hard delete) - Admin/Manager only
  // Blocks if tenant has active contracts
  async deleteTenant(tenantId: string): Promise<ApiResponse<null>> {
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to delete tenant' }
      };
    }

    // Only admin and manager can delete tenants
    if (userContext.role !== 'admin' && userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Access denied: Only admins and managers can delete tenants' }
      };
    }

    // Check for active contracts (only block if status is 'active', allow terminated/expired)
    const { data: activeContracts } = await supabase
      .from('contracts')
      .select('id, status')
      .eq('tenant_id', tenantId)
      .eq('status', 'active')
      .limit(1);

    if (activeContracts && activeContracts.length > 0) {
      return {
        data: null,
        error: { message: 'TENANT_HAS_ACTIVE_CONTRACTS', details: 'deleteWarningContracts' }
      };
    }

    // Delete all non-active contracts for this tenant (terminated/expired/draft)
    // This is needed because of foreign key constraints
    const { error: contractsDeleteError } = await supabase
      .from('contracts')
      .delete()
      .eq('tenant_id', tenantId)
      .neq('status', 'active');

    if (contractsDeleteError) {
      console.error('[API] Error deleting tenant contracts:', contractsDeleteError);
      return {
        data: null,
        error: { message: contractsDeleteError.message, details: 'contractsDeleteFailed' }
      };
    }

    // Call edge function to delete user from auth.users AND profiles
    const { data: session } = await supabase.auth.getSession();
    const accessToken = session?.session?.access_token;

    if (!accessToken) {
      return {
        data: null,
        error: { message: 'No valid session', details: 'authRequired' }
      };
    }

    const { data: funcData, error: funcError } = await supabase.functions.invoke('delete-user', {
      body: { userId: tenantId },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (funcError) {
      console.error('[API] Delete tenant edge function error:', funcError);
      return {
        data: null,
        error: { message: funcError.message, details: 'deleteFailed' }
      };
    }

    if (funcData?.error) {
      console.error('[API] Delete tenant error from function:', funcData.error);
      return {
        data: null,
        error: { message: funcData.error, details: 'tenantNotFoundOrAccessDenied' }
      };
    }

    console.log('[API] Tenant deleted successfully:', tenantId);
    return { data: null, error: null };
  },

  // Delete owner (hard delete) - Admin/Manager only
  // Blocks if owner has properties assigned
  async deleteOwner(ownerId: string): Promise<ApiResponse<null>> {
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to delete owner' }
      };
    }

    // Only admin and manager can delete owners
    if (userContext.role !== 'admin' && userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Access denied: Only admins and managers can delete owners' }
      };
    }

    // Check for assigned properties
    const { data: assignedProperties } = await supabase
      .from('properties')
      .select('id')
      .eq('owner_id', ownerId)
      .limit(1);

    if (assignedProperties && assignedProperties.length > 0) {
      return {
        data: null,
        error: { message: 'OWNER_HAS_PROPERTIES', details: 'deleteWarningProperties' }
      };
    }

    // Call edge function to delete user from auth.users AND profiles
    const { data: session } = await supabase.auth.getSession();
    const accessToken = session?.session?.access_token;

    if (!accessToken) {
      return {
        data: null,
        error: { message: 'No valid session', details: 'authRequired' }
      };
    }

    const { data: funcData, error: funcError } = await supabase.functions.invoke('delete-user', {
      body: { userId: ownerId },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (funcError) {
      console.error('[API] Delete owner edge function error:', funcError);
      return {
        data: null,
        error: { message: funcError.message, details: 'deleteFailed' }
      };
    }

    if (funcData?.error) {
      console.error('[API] Delete owner error from function:', funcData.error);
      return {
        data: null,
        error: { message: funcData.error, details: 'ownerNotFoundOrAccessDenied' }
      };
    }

    console.log('[API] Owner deleted successfully:', ownerId);
    return { data: null, error: null };
  }
};

// Contracts API
export const contractsApi = {
  // Get all contracts
  async getAll(filters?: {
    status?: string;
    property_id?: string;
    tenant_id?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('contracts')
      .select(`
        *,
        property:properties(id, title, address, city, property_code),
        tenant:profiles!contracts_tenant_id_fkey(id, first_name, last_name, email, phone_number)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);
    if (filters?.tenant_id) query = query.eq('tenant_id', filters.tenant_id);

    return handleApiCall(() => query);
  },

  // Get contracts by tenant ID
  async getByTenant(tenantId: string): Promise<ApiResponse<any[]>> {
    return handleApiCall(() => 
      supabase
        .from('contracts')
        .select(`
          *,
          property:properties(id, title, address, city, property_code)
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
    );
  },

  // Get contract by ID
  async getById(id: string): Promise<ApiResponse<any>> {
    return handleApiCall(() => 
      supabase
        .from('contracts')
        .select(`
          *,
          property:properties(
            id, title, description, address, city, area_sqm,
            bedrooms, bathrooms, property_type, property_code,
            is_furnished, status, price
          ),
          tenant:profiles!contracts_tenant_id_fkey(
            id, first_name, last_name, email, phone_number, status
          )
        `)
        .eq('id', id)
        .single()
    );
  },

  // Create contract
  async create(contract: TablesInsert<'contracts'>): Promise<ApiResponse<Tables<'contracts'>>> {
    return handleApiCall(() => 
      supabase
        .from('contracts')
        .insert(contract)
        .select()
        .single()
    );
  },

  // Update contract
  async update(id: string, updates: TablesUpdate<'contracts'>): Promise<ApiResponse<Tables<'contracts'>>> {
    return handleApiCall(() => 
      supabase
        .from('contracts')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
    );
  },

  // Get expiring contracts (next 30 days)
  async getExpiring(): Promise<ApiResponse<any[]>> {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    return handleApiCall(() =>
      supabase
        .from('contracts')
        .select(`
          *,
          property:properties(title, address),
          tenant:profiles!contracts_tenant_id_fkey(first_name, last_name, phone_number)
        `)
        .eq('status', 'active')
        .lte('end_date', thirtyDaysFromNow.toISOString())
        .order('end_date')
    );
  },

  // Get properties for a specific tenant (for tenant dashboard)
  async getTenantProperties(tenantId: string): Promise<ApiResponse<any[]>> {
    return handleApiCall(() =>
      supabase
        .from('contracts')
        .select(`
          property:properties(
            id,
            title,
            address,
            city,
            property_type,
            bedrooms,
            bathrooms,
            area_sqm,
            is_furnished,
            status
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
    ).then(response => {
      // Extract and return only the properties array
      if (response.data) {
        const properties = response.data
          .map((contract: any) => contract.property)
          .filter((property: any) => property); // Filter out null properties
        return { ...response, data: properties };
      }
      return response;
    });
  },

  // Create contract with document and auto-activate
  async createWithDocument(
    contract: TablesInsert<'contracts'>,
    documentUrl?: string
  ): Promise<ApiResponse<Tables<'contracts'>>> {
    const contractData = {
      ...contract,
      contract_document_url: documentUrl || null,
      status: contract.status || 'active', // Default to active
    };

    return handleApiCall(() =>
      supabase
        .from('contracts')
        .insert(contractData)
        .select()
        .single()
    );
  },

  // Update contract document URL
  async updateDocument(
    contractId: string,
    documentUrl: string
  ): Promise<ApiResponse<Tables<'contracts'>>> {
    return handleApiCall(() =>
      supabase
        .from('contracts')
        .update({
          contract_document_url: documentUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', contractId)
        .select()
        .single()
    );
  },

  // Get contract history for a property
  async getPropertyHistory(propertyId: string): Promise<ApiResponse<any[]>> {
    return handleApiCall(() =>
      supabase.rpc('get_property_contract_history', { property_uuid: propertyId })
    );
  },

  // Get contract history for a tenant
  async getTenantHistory(tenantId: string): Promise<ApiResponse<any[]>> {
    return handleApiCall(() =>
      supabase.rpc('get_tenant_contract_history', { tenant_uuid: tenantId })
    );
  },

  // Activate a contract (changes status and updates property)
  async activate(contractId: string): Promise<ApiResponse<Tables<'contracts'>>> {
    return handleApiCall(() =>
      supabase
        .from('contracts')
        .update({
          status: 'active',
          updated_at: new Date().toISOString()
        })
        .eq('id', contractId)
        .select()
        .single()
    );
  },

  // Terminate a contract (sets status and frees property)
  async terminate(contractId: string, reason?: string): Promise<ApiResponse<Tables<'contracts'>>> {
    return handleApiCall(() =>
      supabase
        .from('contracts')
        .update({
          status: 'terminated',
          updated_at: new Date().toISOString()
        })
        .eq('id', contractId)
        .select()
        .single()
    );
  },

  // Manually expire contracts (calls database function)
  async expireContracts(): Promise<ApiResponse<{ expired_count: number; contracts_expired: string[] }>> {
    return handleApiCall(() =>
      supabase.rpc('expire_contracts')
    );
  }
};

// Maintenance API
export const maintenanceApi = {
  // Get maintenance requests with role-based access control
  async getRequests(filters?: {
    status?: string;
    priority?: string;
    property_id?: string;
  }): Promise<ApiResponse<any[]>> {
    // **SECURITY**: Get current user context for role-based filtering
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access maintenance requests' }
      };
    }

    if (!['admin', 'manager', 'owner', 'tenant'].includes(userContext.role)) {
      return { data: null, error: { message: 'Access denied: You do not have permission to access maintenance requests' } };
    }

    let query = supabase
      .from('maintenance_requests')
      .select(`
        *,
        property:properties(title, address, property_code, property_type),
        tenant:profiles!maintenance_requests_tenant_id_fkey(first_name, last_name, phone_number),
        work_orders(*)
      `)
      .order('created_at', { ascending: false });

    // **SECURITY**: Apply role-based filtering FIRST
    if (userContext.role === 'tenant') {
      // Tenants can ONLY see maintenance requests for properties they are renting
      const rentedPropertyIds = userContext.rentedPropertyIds || [];
      if (rentedPropertyIds.length > 0) {
        query = query.in('property_id', rentedPropertyIds);
      } else {
        // If tenant has no rented properties, return empty result immediately
        return {
          data: [],
          error: null,
          count: 0
        };
      }
    } else if (userContext.role === 'owner') {
      // Owners see maintenance requests for their properties only
      const ownedPropertyIds = userContext.ownedPropertyIds || [];
      if (ownedPropertyIds.length > 0) {
        query = query.in('property_id', ownedPropertyIds);
      } else {
        // If owner has no properties, return empty result immediately
        return {
          data: [],
          error: null,
          count: 0
        };
      }
    }
    // Admins and managers see all requests (no additional filtering)

    // Apply additional filters
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.priority) query = query.eq('priority', filters.priority);
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);
    return handleApiCall(() => query);
  },

  // Get single maintenance request by ID with access control
  async getById(id: string): Promise<ApiResponse<any>> {
    // **SECURITY**: Get current user context for access validation
    const userContext = await getCurrentUserContext();

    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access maintenance request' }
      };
    }

    const { data, error } = await supabase
      .from('maintenance_requests')
      .select(`
        *,
        property:properties(title, address, property_code, property_type),
        tenant:profiles!maintenance_requests_tenant_id_fkey(first_name, last_name, phone_number),
        work_orders(*)
      `)
      .eq('id', id)
      .single();

    if (error) {
      return { data: null, error };
    }

    if (!data) {
      return {
        data: null,
        error: { message: 'Maintenance request not found' }
      };
    }

    // **SECURITY**: Validate access based on user role
    let hasAccess = false;

    if (userContext.role === 'admin' || userContext.role === 'manager') {
      hasAccess = true;
    } else if (userContext.role === 'tenant') {
      const rentedPropertyIds = userContext.rentedPropertyIds || [];
      hasAccess = rentedPropertyIds.includes(data.property_id);
    } else if (userContext.role === 'owner') {
      const ownedPropertyIds = userContext.ownedPropertyIds || [];
      hasAccess = ownedPropertyIds.includes(data.property_id);
    }

    if (!hasAccess) {
      return {
        data: null,
        error: { message: 'Access denied: You do not have permission to view this maintenance request' }
      };
    }

    return { data, error: null };
  },

  // Create maintenance request with access control
  async createRequest(request: TablesInsert<'maintenance_requests'>): Promise<ApiResponse<Tables<'maintenance_requests'>>> {
    // **SECURITY**: Get current user context for access validation
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to create maintenance request' }
      };
    }

    if (!['admin', 'manager', 'owner', 'tenant'].includes(userContext.role)) {
      return { data: null, error: { message: 'Access denied: You do not have permission to create maintenance requests' } };
    }

    // **SECURITY**: Validate that user can create request for this property
    if (userContext.role === 'tenant') {
      const rentedPropertyIds = userContext.rentedPropertyIds || [];
      if (!rentedPropertyIds.includes(request.property_id!)) {
        return {
          data: null,
          error: { message: 'Access denied: You can only create maintenance requests for properties you are renting' }
        };
      }
      // Ensure tenant_id is set to current user for tenant requests
      request = { ...request, tenant_id: userContext.userId };
    } else if (userContext.role === 'owner') {
      const ownedPropertyIds = userContext.ownedPropertyIds || [];
      if (!ownedPropertyIds.includes(request.property_id!)) {
        return {
          data: null,
          error: { message: 'Access denied: You can only create maintenance requests for properties you own' }
        };
      }
    }
    // Admins and managers can create requests for any property
    
    return handleApiCall(() => 
      supabase
        .from('maintenance_requests')
        .insert(request)
        .select()
        .single()
    );
  },

  // Update maintenance request with access control
  async updateRequest(id: string, updates: TablesUpdate<'maintenance_requests'>): Promise<ApiResponse<Tables<'maintenance_requests'>>> {
    // **SECURITY**: Get current user context for access validation
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to update maintenance request' }
      };
    }

    // **SECURITY**: First check if the maintenance request exists and user has access
    const { data: existingRequest } = await supabase
      .from('maintenance_requests')
      .select('property_id, tenant_id')
      .eq('id', id)
      .single();

    if (!existingRequest) {
      return {
        data: null,
        error: { message: 'Maintenance request not found' }
      };
    }

    // **SECURITY**: Validate access based on user role
    let hasAccess = false;
    
    if (userContext.role === 'tenant') {
      // Tenants can only update requests they created for their rented properties
      const rentedPropertyIds = userContext.rentedPropertyIds || [];
      hasAccess = existingRequest.tenant_id === userContext.userId && 
                  rentedPropertyIds.includes(existingRequest.property_id);
    } else if (userContext.role === 'owner') {
      // Owners can update requests for their properties
      const ownedPropertyIds = userContext.ownedPropertyIds || [];
      hasAccess = ownedPropertyIds.includes(existingRequest.property_id);
    } else if (userContext.role === 'admin' || userContext.role === 'manager') {
      // Admins and managers can update any request
      hasAccess = true;
    }

    if (!hasAccess) {
      return {
        data: null,
        error: { message: 'Access denied: You do not have permission to update this maintenance request' }
      };
    }

    // Non-staff may edit an accessible request, not move it to another record or actor.
    if (userContext.role !== 'admin' && userContext.role !== 'manager' && (
      (updates.id !== undefined && updates.id !== id) ||
      (updates.property_id !== undefined && updates.property_id !== existingRequest.property_id) ||
      (updates.tenant_id !== undefined && updates.tenant_id !== existingRequest.tenant_id)
    )) {
      return { data: null, error: { message: 'Access denied: You cannot reassign this maintenance request' } };
    }

    return handleApiCall(() => 
      supabase
        .from('maintenance_requests')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
    );
  },

  // Get all work orders
  async getWorkOrders(filters?: {
    status?: string;
    assigned_to?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('work_orders')
      .select(`
        *,
        maintenance_request:maintenance_requests(
          *,
          property:properties(title, address, property_code),
          tenant:profiles!maintenance_requests_tenant_id_fkey(first_name, last_name)
        ),
        assigned:profiles!work_orders_assigned_to_fkey(first_name, last_name, phone_number)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.assigned_to) query = query.eq('assigned_to', filters.assigned_to);

    return handleApiCall(() => query);
  },

  // Create work order
  async createWorkOrder(workOrder: TablesInsert<'work_orders'>): Promise<ApiResponse<Tables<'work_orders'>>> {
    return handleApiCall(async () => {
      const context = await getCurrentUserContext();
      if (!context || !['admin', 'manager'].includes(context.role)) throw new Error('Access denied: Work orders require admin or manager');
      return supabase
        .from('work_orders')
        .insert(workOrder)
        .select()
        .single()
    });
  },

  // Update work order
  async updateWorkOrder(id: string, updates: TablesUpdate<'work_orders'>): Promise<ApiResponse<Tables<'work_orders'>>> {
    return handleApiCall(async () => {
      const context = await getCurrentUserContext();
      if (!context || !['admin', 'manager'].includes(context.role)) throw new Error('Access denied: Work orders require admin or manager');
      return supabase
        .from('work_orders')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
    });
  }
};

// Accounts API
export const accountsApi = {
  // Get all accounts (chart of accounts)
  async getAll(filters?: {
    account_type?: string;
    is_active?: boolean;
  }): Promise<ApiResponse<Tables<'accounts'>[]>> {
    let query = supabase
      .from('accounts')
      .select('*')
      .order('account_code');

    if (filters?.account_type) query = query.eq('account_type', filters.account_type);
    if (filters?.is_active !== undefined) query = query.eq('is_active', filters.is_active);

    return handleApiCall(() => query);
  },

  // Get accounts by type (for specific voucher types)
  async getByType(accountType: string): Promise<ApiResponse<Tables<'accounts'>[]>> {
    return handleApiCall(() => 
      supabase
        .from('accounts')
        .select('*')
        .eq('account_type', accountType)
        .eq('is_active', true)
        .order('account_code')
    );
  },

  // Get revenue accounts (for receipt vouchers)
  async getRevenueAccounts(): Promise<ApiResponse<Tables<'accounts'>[]>> {
    return this.getByType('revenue');
  },

  // Get expense accounts (for payment vouchers)
  async getExpenseAccounts(): Promise<ApiResponse<Tables<'accounts'>[]>> {
    return this.getByType('expense');
  },

  // Get asset accounts (for receipt vouchers - cash accounts)
  async getAssetAccounts(): Promise<ApiResponse<Tables<'accounts'>[]>> {
    return this.getByType('asset');
  }
};

// Cost Centers API
export const costCentersApi = {
  // Get all cost centers
  async getAll(filters?: {
    is_active?: boolean;
  }): Promise<ApiResponse<Tables<'cost_centers'>[]>> {
    let query = supabase
      .from('cost_centers')
      .select('*')
      .order('code');

    if (filters?.is_active !== undefined) query = query.eq('is_active', filters.is_active);

    return handleApiCall(() => query);
  },

  // Get active cost centers only
  async getActive(): Promise<ApiResponse<Tables<'cost_centers'>[]>> {
    return this.getAll({ is_active: true });
  }
};

// Vouchers API
export const vouchersApi = {
  // Additive detailed workflow; keep legacy create unchanged for existing integrations.
  async requestDetailedId() {
    try { return { data: await detailedVoucherRequestId(supabase), error: null }; }
    catch (error) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async getDetailedTenants(propertyId: string) {
    try { return { data: await detailedVoucherTenants(supabase,propertyId), error: null }; }
    catch (error) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async createDetailed(input: DetailedVoucherInput, requestId: string, current: () => boolean) {
    try { return { data: await createDetailedVoucher(supabase,input,requestId,current), error: null }; }
    catch (error) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async getDetailed(id: string) {
    try { return { data: await readDetailedVoucher(supabase,id), error: null }; }
    catch (error) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async probeDetailed(id: string) {
    try { return { data: await probeDetailedVoucher(supabase,id), error: null }; }
    catch (error) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async getLegacyDraft(id: string) {
    try { return { data: await readLegacyVoucherDraft(supabase,id), error: null }; }
    catch (error: any) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async updateLegacyDraft(id: string, updatedAt: string, input: LegacyVoucherDraftInput, requestId: string, current: () => boolean) {
    try { return { data: await updateLegacyVoucherDraft(supabase,id,updatedAt,input,requestId,current), error: null }; }
    catch (error: any) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async updateDetailed(id: string, revision: number, input: DetailedVoucherInput, requestId: string, current: () => boolean) {
    try { return { data: await updateDetailedVoucher(supabase,id,revision,input,requestId,current), error: null }; }
    catch (error) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  async transitionDetailed(id: string, revision: number, status: 'posted' | 'cancelled', reason: string | null, requestId: string, current: () => boolean) {
    try { return { data: await transitionDetailedVoucher(supabase,id,revision,status,reason,requestId,current), error: null }; }
    catch (error) { return { data: null, error: { message: error.message, code: error.code } }; }
  },
  // Get all vouchers
  // Note: vouchers schema only has: id, voucher_type, voucher_number, amount, currency, status, description,
  //       property_id, tenant_id, created_by, created_at, updated_at
  // accounts and cost_centers tables don't exist in the database
  async getAll(filters?: {
    voucher_type?: string;
    status?: string;
    property_id?: string;
    tenant_id?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('vouchers')
      .select(`
        *,
        property:properties(title, address),
        tenant:profiles!vouchers_tenant_id_fkey(first_name, last_name),
        created_by_user:profiles!vouchers_created_by_fkey(first_name, last_name)
      `)
      .order('created_at', { ascending: false });

    if (filters?.voucher_type) query = query.eq('voucher_type', filters.voucher_type);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);
    if (filters?.tenant_id) query = query.eq('tenant_id', filters.tenant_id);

    return handleApiCall(() => query);
  },

  // Generate voucher number
  async generateVoucherNumber(voucherType: string): Promise<string> {
    const prefix = {
      'receipt': 'RCP',
      'payment': 'PAY', 
      'journal': 'JNL'
    }[voucherType] || 'VCH';
    
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const timestamp = Date.now().toString().slice(-6);
    
    return `${prefix}-${year}${month}-${timestamp}`;
  },

  // Create voucher
  async create(voucher: TablesInsert<'vouchers'>): Promise<ApiResponse<Tables<'vouchers'>>> {
    return handleApiCall(() => 
      supabase
        .from('vouchers')
        .insert(voucher)
        .select()
        .single()
    );
  },

  // Get voucher summary
  async getSummary(period?: 'month' | 'year'): Promise<ApiResponse<any>> {
    const periodFilter = period === 'month' ? 30 : 365;
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - periodFilter);

    return handleApiCall(async () => {
      const result = await supabase
        .from('vouchers')
        .select('voucher_type, amount')
        .gte('created_at', dateFrom.toISOString());

      if (result.error) throw result.error;

      const vouchers = result.data || [];
      const summary = {
        total_receipts: vouchers.filter(v => v.voucher_type === 'receipt').reduce((sum, v) => sum + v.amount, 0),
        total_payments: vouchers.filter(v => v.voucher_type === 'payment').reduce((sum, v) => sum + v.amount, 0),
        total_journals: vouchers.filter(v => v.voucher_type === 'journal').reduce((sum, v) => sum + v.amount, 0),
        count_receipts: vouchers.filter(v => v.voucher_type === 'receipt').length,
        count_payments: vouchers.filter(v => v.voucher_type === 'payment').length,
        count_journals: vouchers.filter(v => v.voucher_type === 'journal').length
      };

      return { data: summary, error: null };
    });
  },

  // Enhanced filtering with search
  // Note: accounts and cost_centers tables don't exist in the database
  async getAllWithSearch(filters?: {
    voucher_type?: string;
    status?: string;
    property_id?: string;
    tenant_id?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('vouchers')
      .select(`
        *,
        property:properties(title, address),
        tenant:profiles!vouchers_tenant_id_fkey(first_name, last_name, email),
        created_by_user:profiles!vouchers_created_by_fkey(first_name, last_name)
      `)
      .order('created_at', { ascending: false });

    if (filters?.voucher_type) query = query.eq('voucher_type', filters.voucher_type);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);
    if (filters?.tenant_id) query = query.eq('tenant_id', filters.tenant_id);
    if (filters?.startDate) query = query.gte('created_at', filters.startDate);
    if (filters?.endDate) query = query.lte('created_at', filters.endDate);
    if (filters?.search) {
      query = query.or(`voucher_number.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    return handleApiCall(() => query);
  },

  // Get voucher by ID with detailed information
  // Note: accounts and cost_centers tables don't exist; profiles uses phone_number not phone
  async getById(id: string): Promise<ApiResponse<any>> {
    return handleApiCall(() =>
      supabase
        .from('vouchers')
        .select(`
          *,
          property:properties(id, title, address, city, property_type),
          tenant:profiles!vouchers_tenant_id_fkey(id, first_name, last_name, email, phone_number),
          created_by_user:profiles!vouchers_created_by_fkey(id, first_name, last_name, email)
        `)
        .eq('id', id)
        .single()
    );
  },

  // Update voucher status with authorization
  async updateStatus(id: string, status: 'draft' | 'posted' | 'cancelled', notes?: string): Promise<ApiResponse<any>> {
    const updateData: any = {
      status,
      updated_at: new Date().toISOString()
    };

    if (status === 'posted') {
      updateData.posted_at = new Date().toISOString();
    }

    if (status === 'cancelled') {
      updateData.cancelled_at = new Date().toISOString();
      if (notes) updateData.cancellation_notes = notes;
    }

    return handleApiCall(() =>
      supabase
        .from('vouchers')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          property:properties(title, address),
          tenant:profiles!vouchers_tenant_id_fkey(first_name, last_name)
        `)
        .single()
    );
  },

  // Duplicate voucher for recurring transactions
  async duplicate(id: string): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      // First get the original voucher
      const originalResult = await supabase
        .from('vouchers')
        .select('*')
        .eq('id', id)
        .single();

      if (originalResult.error) throw originalResult.error;

      const original = originalResult.data;

      // Generate new voucher number
      const newVoucherNumber = await this.generateVoucherNumber(original.voucher_type);

      // Create duplicate with new number and draft status
      const duplicateData = {
        ...original,
        id: undefined, // Let database generate new ID
        voucher_number: newVoucherNumber,
        status: 'draft',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        posted_at: null,
        cancelled_at: null,
        cancellation_notes: null
      };

      const result = await supabase
        .from('vouchers')
        .insert([duplicateData])
        .select(`
          *,
          property:properties(title, address),
          tenant:profiles!vouchers_tenant_id_fkey(first_name, last_name)
        `)
        .single();

      return result;
    });
  },

  // Get voucher statistics for management dashboard
  async getStatistics(): Promise<ApiResponse<{
    totalVouchers: number;
    draftVouchers: number;
    postedVouchers: number;
    cancelledVouchers: number;
    totalAmount: number;
    receiptAmount: number;
    paymentAmount: number;
    journalAmount: number;
    monthlyTrend: Array<{ month: string; count: number; amount: number }>;
  }>> {
    return handleApiCall(async () => {
      const vouchersResult = await supabase
        .from('vouchers')
        .select('status, voucher_type, amount, created_at');

      if (vouchersResult.error) throw vouchersResult.error;

      const vouchers = vouchersResult.data || [];

      // Calculate monthly trend (last 6 months)
      const monthlyTrend = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        
        const monthVouchers = vouchers.filter(v => {
          const vDate = new Date(v.created_at);
          return vDate >= monthStart && vDate <= monthEnd;
        });

        monthlyTrend.push({
          month: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          count: monthVouchers.length,
          amount: monthVouchers.reduce((sum, v) => sum + (v.amount || 0), 0)
        });
      }

      const stats = {
        totalVouchers: vouchers.length,
        draftVouchers: vouchers.filter(v => v.status === 'draft').length,
        postedVouchers: vouchers.filter(v => v.status === 'posted').length,
        cancelledVouchers: vouchers.filter(v => v.status === 'cancelled').length,
        totalAmount: vouchers.reduce((sum, v) => sum + (v.amount || 0), 0),
        receiptAmount: vouchers.filter(v => v.voucher_type === 'receipt').reduce((sum, v) => sum + (v.amount || 0), 0),
        paymentAmount: vouchers.filter(v => v.voucher_type === 'payment').reduce((sum, v) => sum + (v.amount || 0), 0),
        journalAmount: vouchers.filter(v => v.voucher_type === 'journal').reduce((sum, v) => sum + (v.amount || 0), 0),
        monthlyTrend
      };

      return { data: stats, error: null };
    });
  },

  // Update voucher (for editing draft vouchers)
  // Note: accounts and cost_centers tables don't exist on the remote database
  async update(id: string, updates: Partial<TablesUpdate<'vouchers'>>): Promise<ApiResponse<any>> {
    return handleApiCall(() =>
      supabase
        .from('vouchers')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select(`
          *,
          property:properties(title, address),
          tenant:profiles!vouchers_tenant_id_fkey(first_name, last_name)
        `)
        .single()
    );
  },

  // Delete voucher (only for draft vouchers)
  async delete(id: string): Promise<ApiResponse<null>> {
    return handleApiCall(() => 
      supabase
        .from('vouchers')
        .delete()
        .eq('id', id)
        .eq('status', 'draft') // Only allow deletion of draft vouchers
    );
  }
};

// Invoices API
export const invoicesApi = {
  // Detailed creation is additive; legacy create below remains unchanged.
  async createDetailed(input: {
    invoice_number: string; tenant_id: string; property_id: string | null;
    issue_date: string; due_date: string; status: 'draft' | 'pending'; currency: 'SAR';
    vat_rate: number; vat_included: boolean; amount: number; vat_amount: number; total_amount: number;
    lines: Array<{ description: string; quantity: string; unit_price: string }>;
  }, requestId: string, current: () => boolean): Promise<ApiResponse<{ id: string; invoice_number: string }>> {
    return handleApiCall(async () => {
      if (!current()) throw new Error('Invoice actor changed');
      const actor = await getCurrentUserContext();
      if (!current() || !actor || !['admin', 'manager', 'accountant'].includes(actor.role)) throw new Error('Invoice access denied');
      const session = await supabase.auth.getSession();
      if (!current() || session.error || session.data.session?.user.id !== actor.userId) throw new Error('Invoice actor changed');
      const result = await supabase.rpc('invoice_detail_create', { p_request_id: requestId, p_payload: input });
      const after = await supabase.auth.getSession();
      if (!current() || after.error || after.data.session?.user.id !== actor.userId) throw new Error('Invoice actor changed');
      if (!result.error && (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(result.data?.id || '') || result.data.invoice_number !== input.invoice_number)) throw new Error('Invoice confirmation missing');
      return result;
    });
  },
  async readDetails(id: string): Promise<ApiResponse<any>> {
    return handleApiCall(() => supabase.rpc('invoice_detail_read', { p_id: id }));
  },
  // Get all invoices
  async getAll(filters?: {
    status?: string;
    property_id?: string;
    tenant_id?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('invoices')
      .select(`
        *,
        property:properties(title, address, property_code),
        tenant:profiles!invoices_tenant_id_fkey(first_name, last_name, email, phone_number)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);
    if (filters?.tenant_id) query = query.eq('tenant_id', filters.tenant_id);

    return handleApiCall(() => query);
  },

  // Get invoice by ID
  // Note: profiles table only has: id, first_name, last_name, email, phone_number, role, avatar_url, created_at, updated_at
  async getById(id: string): Promise<ApiResponse<any>> {
    return handleApiCall(() =>
      supabase
        .from('invoices')
        .select(`
          *,
          property:properties(id, title, address, city, property_type, area_sqm),
          tenant:profiles!invoices_tenant_id_fkey(id, first_name, last_name, email, phone_number)
        `)
        .eq('id', id)
        .single()
    );
  },

  // Create invoice
  async create(invoice: TablesInsert<'invoices'>): Promise<ApiResponse<Tables<'invoices'>>> {
    return handleApiCall(() => 
      supabase
        .from('invoices')
        .insert(invoice)
        .select()
        .single()
    );
  },

  // Update invoice
  async update(id: string, updates: TablesUpdate<'invoices'>): Promise<ApiResponse<Tables<'invoices'>>> {
    return handleApiCall(() => 
      supabase
        .from('invoices')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
    );
  },

  // Generate unique invoice number
  async generateInvoiceNumber(): Promise<string> {
    try {
      const year = new Date().getFullYear();
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number')
        .like('invoice_number', `INV-${year}-%`)
        .order('invoice_number', { ascending: false })
        .limit(1);

      if (error) throw error;

      let nextNumber = 1;
      if (data && data.length > 0) {
        const lastNumber = data[0].invoice_number;
        const numberPart = lastNumber.split('-')[2];
        nextNumber = parseInt(numberPart) + 1;
      }

      return `INV-${year}-${nextNumber.toString().padStart(6, '0')}`;
    } catch (error) {
      console.error('Error generating invoice number:', error);
      const year = new Date().getFullYear();
      const random = Math.floor(Math.random() * 1000000);
      return `INV-${year}-${random.toString().padStart(6, '0')}`;
    }
  },

  // Get overdue invoices
  async getOverdue(): Promise<ApiResponse<any[]>> {
    const today = new Date().toISOString().split('T')[0];
    
    return handleApiCall(() => 
      supabase
        .from('invoices')
        .select(`
          *,
          property:properties(title, address),
          tenant:profiles!invoices_tenant_id_fkey(first_name, last_name, phone_number)
        `)
        .eq('status', 'pending')
        .lt('due_date', today)
        .order('due_date')
    );
  },

  // Get invoices by status
  async getByStatus(status: string): Promise<ApiResponse<any[]>> {
    return handleApiCall(() => 
      supabase
        .from('invoices')
        .select(`
          *,
          property:properties(title, address),
          tenant:profiles!invoices_tenant_id_fkey(first_name, last_name, email)
        `)
        .eq('status', status)
        .order('created_at', { ascending: false })
    );
  },

  // Calculate VAT for amount
  calculateVAT: (amount: number, vatRate: number = 15) => {
    const vatMultiplier = vatRate / 100;
    const netAmount = amount;
    const vatAmount = amount * vatMultiplier;
    const grossAmount = amount + vatAmount;
    
    return {
      netAmount: Math.round(netAmount * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
      grossAmount: Math.round(grossAmount * 100) / 100,
    };
  },

  // Calculate invoice totals with line items
  calculateInvoiceTotals: (lineItems: any[], vatRate: number = 15) => {
    const lineItemTotals = lineItems.map(item => {
      const lineTotal = (item.quantity || 1) * (item.unit_price || 0);
      const vatAmount = lineTotal * (vatRate / 100);
      const lineTotalWithVAT = lineTotal + vatAmount;
      
      return {
        ...item,
        line_total: Math.round(lineTotal * 100) / 100,
        vat_amount: Math.round(vatAmount * 100) / 100,
        total_with_vat: Math.round(lineTotalWithVAT * 100) / 100,
      };
    });

    const subtotal = lineItemTotals.reduce((sum, item) => sum + item.line_total, 0);
    const totalVAT = lineItemTotals.reduce((sum, item) => sum + item.vat_amount, 0);
    const totalAmount = subtotal + totalVAT;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalVAT: Math.round(totalVAT * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
      lineItemTotals,
    };
  },

  // Calculate due date based on payment terms
  calculateDueDate: (issueDate: string, paymentTerms: string) => {
    const issue = new Date(issueDate);
    let daysToAdd = 30; // Default to 30 days

    if (paymentTerms.includes('15')) daysToAdd = 15;
    else if (paymentTerms.includes('30')) daysToAdd = 30;
    else if (paymentTerms.includes('60')) daysToAdd = 60;
    else if (paymentTerms.includes('90')) daysToAdd = 90;

    const dueDate = new Date(issue);
    dueDate.setDate(dueDate.getDate() + daysToAdd);
    
    return dueDate.toISOString().split('T')[0];
  }
};

// Issues API
export const issuesApi = {
  // Get all issues
  async getAll(filters?: {
    status?: string;
    priority?: string;
    property_id?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('issues')
      .select(`
        *,
        property:properties(title, address, property_code),
        reported_by_user:profiles!issues_reported_by_fkey(first_name, last_name)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.priority) query = query.eq('priority', filters.priority);
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);

    return handleApiCall(() => query);
  },

  // Create issue
  async create(issue: TablesInsert<'issues'>): Promise<ApiResponse<Tables<'issues'>>> {
    return handleApiCall(() => 
      supabase
        .from('issues')
        .insert(issue)
        .select()
        .single()
    );
  },

  // Update issue
  async update(id: string, updates: TablesUpdate<'issues'>): Promise<ApiResponse<Tables<'issues'>>> {
    return handleApiCall(() => 
      supabase
        .from('issues')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
    );
  }
};

// Documents API
export const documentsApi = {
  // Get all documents
  async getAll(filters?: {
    document_type?: string;
    related_entity_type?: string;
    related_entity_id?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('documents')
      .select(`
        *,
        uploaded_by_user:profiles!documents_uploaded_by_fkey(first_name, last_name)
      `)
      .order('created_at', { ascending: false });

    if (filters?.document_type) query = query.eq('document_type', filters.document_type);
    if (filters?.related_entity_type) query = query.eq('related_entity_type', filters.related_entity_type);
    if (filters?.related_entity_id) query = query.eq('related_entity_id', filters.related_entity_id);

    return handleApiCall(() => query);
  },

  // Get document by ID
  async getById(id: string): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      const result = await supabase
        .from('documents')
        .select(`
          *,
          uploaded_by_user:profiles!documents_uploaded_by_fkey(first_name, last_name)
        `)
        .eq('id', id)
        .single();
      if (result.error || !result.data) return result;
      return { data: { ...result.data, file_path: await resolveDocumentUrl(result.data.file_path) }, error: null };
    });
  },

  // Create document
  async create(document: TablesInsert<'documents'>): Promise<ApiResponse<Tables<'documents'>>> {
    const context = await getCurrentUserContext();
    if (!context || !['admin', 'manager', 'owner', 'tenant'].includes(context.role)) {
      return { data: null, error: { message: 'Access denied: You cannot upload documents' } };
    }
    return handleApiCall(() => 
      supabase
        .from('documents')
        .insert({ ...document, uploaded_by: context.userId })
        .select()
        .single()
    );
  }
};

// Clients API
export const clientsApi = {
  // Get all clients
  async getAll(filters?: {
    client_type?: string;
    status?: string;
  }): Promise<ApiResponse<Tables<'clients'>[]>> {
    let query = supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.client_type) query = query.eq('client_type', filters.client_type);
    if (filters?.status) query = query.eq('status', filters.status);

    return handleApiCall(() => query);
  },

  // Create client
  async create(client: TablesInsert<'clients'>): Promise<ApiResponse<Tables<'clients'>>> {
    return handleApiCall(() => 
      supabase
        .from('clients')
        .insert(client)
        .select()
        .single()
    );
  }
};

// Property Reservations API
export const reservationsApi = {
  // Get all reservations
  async getAll(filters?: {
    status?: string;
    property_id?: string;
  }): Promise<ApiResponse<any[]>> {
    let query = supabase
      .from('property_reservations')
      .select(`
        *,
        property:properties(title, address, property_code),
        client:clients(company_name, contact_person, email, phone)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);

    return handleApiCall(() => query);
  },

  // Create reservation
  async create(reservation: TablesInsert<'property_reservations'>): Promise<ApiResponse<Tables<'property_reservations'>>> {
    return handleApiCall(() => 
      supabase
        .from('property_reservations')
        .insert(reservation)
        .select()
        .single()
    );
  }
};

// Reports API
export const reportsApi = {
  // Only the report catalogue is known. Generation telemetry is not recorded.
  async getStats(userRole?: string): Promise<ApiResponse<{
    totalReports: number;
    generatedThisMonth: number | null;
    scheduledReports: number | null;
    avgGenerationTime: string | null;
  }>> {
    return {
      data: {
        totalReports: ['admin', 'manager', 'owner'].includes(userRole || '') ? 11 : 0,
        generatedThisMonth: null,
        scheduledReports: null,
        avgGenerationTime: null,
      },
      error: null,
    };
  },

  // Revenue Report Data
  async getRevenueReport(startDate?: string, endDate?: string, currency = 'SAR', amountBounds: ReportAmountBounds = {}): Promise<ApiResponse<{
    totalRevenue: number;
    monthlyBreakdown: Array<{ month: string; monthNumber: number; revenue: number; year: number }>;
    propertyBreakdown: Array<{ propertyId: string; propertyTitle: string; revenue: number }>;
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      // Get receipt vouchers (income) for the period
      const startDateFilter = startDate || new Date(new Date().getFullYear(), 0, 1).toISOString();
      const endDateFilter = endDate || new Date().toISOString();

      validateReportAmountBounds(amountBounds);
      const { data: vouchers, error: vouchersError } = await readReportRows<any>((from, to) => applyReportAmountBounds(supabase
        .from('vouchers')
        .select(`
          amount,
          created_at,
          property_id,
          property:properties(id, title)
        `)
        .eq('voucher_type', 'receipt')
        .eq('status', 'posted')
        .eq('currency', currency)
        .gte('created_at', startDateFilter)
        .lte('created_at', endDateFilter), amountBounds).order('id').range(from, to));

      if (vouchersError) throw vouchersError;

      const totalRevenue = (vouchers?.reduce((sum, v) => sum + Math.round(Number(v.amount || 0) * 100), 0) || 0) / 100;

      // Group by month
      const monthlyData: { [key: string]: number } = {};
      const propertyData: { [key: string]: { title: string; revenue: number } } = {};

      vouchers?.forEach(voucher => {
        const date = new Date(voucher.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + Math.round(Number(voucher.amount || 0) * 100);

        if (voucher.property_id && voucher.property) {
          if (!propertyData[voucher.property_id]) {
            propertyData[voucher.property_id] = {
              title: voucher.property.title || '',
              revenue: 0
            };
          }
          propertyData[voucher.property_id].revenue += Math.round(Number(voucher.amount || 0) * 100);
        }
      });

      const monthlyBreakdown = Object.entries(monthlyData).sort(([a], [b]) => a.localeCompare(b)).map(([monthKey, revenue]) => {
        const [year, month] = monthKey.split('-');
        return {
          month: new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' }),
          year: parseInt(year),
          monthNumber: parseInt(month),
          revenue: revenue / 100
        };
      });

      const propertyBreakdown = Object.entries(propertyData).map(([propertyId, data]) => ({
        propertyId,
        propertyTitle: data.title,
        revenue: data.revenue / 100
      }));

      return {
        data: {
          totalRevenue,
          monthlyBreakdown,
          propertyBreakdown,
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Expense Report Data
  // UPDATED: Added expenseType parameter to filter by expense category
  async getExpenseReport(startDate?: string, endDate?: string, expenseType?: 'sales' | 'maintenance' | 'all', currency = 'SAR', amountBounds: ReportAmountBounds = {}): Promise<ApiResponse<{
    totalExpenses: number;
    monthlyBreakdown: Array<{ month: string; monthNumber: number; expenses: number; year: number }>;
    categoryBreakdown: Array<{ category: string; amount: number }>;
    expenseType?: string;
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      const startDateFilter = startDate || new Date(new Date().getFullYear(), 0, 1).toISOString();
      const endDateFilter = endDate || new Date().toISOString();
      const filterType = expenseType || 'all';
      if (filterType !== 'all') {
        throw new Error('Expense category filtering is unavailable until vouchers have verified category data.');
      }

      // Note: accounts table doesn't exist on remote, using description for categorization
      validateReportAmountBounds(amountBounds);
      const { data: vouchers, error: vouchersError } = await readReportRows<any>((from, to) => applyReportAmountBounds(supabase
        .from('vouchers')
        .select(`
          amount,
          created_at,
          description
        `)
        .eq('voucher_type', 'payment')
        .eq('status', 'posted')
        .eq('currency', currency)
        .gte('created_at', startDateFilter)
        .lte('created_at', endDateFilter), amountBounds).order('id').range(from, to));

      if (vouchersError) throw vouchersError;

      // All payment vouchers are expenses since we don't have account types
      const filteredVouchers = vouchers || [];

      const totalExpenses = filteredVouchers.reduce((sum, v) => sum + Math.round(Number(v.amount || 0) * 100), 0) / 100;

      // Group by month and category (using description as category)
      const monthlyData: { [key: string]: number } = {};
      const categoryData: { [key: string]: number } = {};

      filteredVouchers.forEach(voucher => {
        const date = new Date(voucher.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + Math.round(Number(voucher.amount || 0) * 100);

        const category = voucher.description || '';
        categoryData[category] = (categoryData[category] || 0) + Math.round(Number(voucher.amount || 0) * 100);
      });

      const monthlyBreakdown = Object.entries(monthlyData).sort(([a], [b]) => a.localeCompare(b)).map(([monthKey, expenses]) => {
        const [year, month] = monthKey.split('-');
        return {
          month: new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' }),
          year: parseInt(year),
          monthNumber: parseInt(month),
          expenses: expenses / 100
        };
      });

      const categoryBreakdown = Object.entries(categoryData).map(([category, amount]) => ({
        category,
        amount: amount / 100
      }));

      return {
        data: {
          totalExpenses,
          monthlyBreakdown,
          categoryBreakdown,
          expenseType: filterType, // Include the filter type in the response
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Property Performance Report
  // UPDATED: Fixed occupancy calculation and maintenance cost filtering
  async getPropertyPerformanceReport(): Promise<ApiResponse<{
    properties: Array<{
      id: string;
      title: string;
      occupancyRate: number;
      totalUnits: number;
      occupiedUnits: number;
      monthlyRevenue: number;
      maintenanceCosts: number;
      netIncome: number;
      roi: number;
    }>;
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      // Note: accounts table doesn't exist on remote, not joining with accounts
      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select(`
          id,
          title,
          status,
          annual_rent,
          price,
          total_units,
          contracts(status, rent_amount, start_date, end_date),
          vouchers(amount, voucher_type, created_at),
          maintenance_requests(id)
        `);

      if (propertiesError) throw propertiesError;

      const performanceData = properties?.map(property => {
        const activeContracts = property.contracts?.filter(c => c.status === 'active') || [];

        // Calculate actual occupancy percentage based on total units
        const totalUnits = property.total_units || 1;
        const occupiedUnits = activeContracts.length;
        const occupancyRate = Math.round((occupiedUnits / totalUnits) * 100);

        const monthlyRevenue = activeContracts.reduce((sum, c) => sum + (c.rent_amount || 0), 0);

        // Count all payment vouchers as maintenance costs (since we don't have account types)
        const maintenanceCosts = property.vouchers
          ?.filter(v => v.voucher_type === 'payment')
          ?.reduce((sum, v) => sum + (v.amount || 0), 0) || 0;

        const netIncome = monthlyRevenue - maintenanceCosts;
        const propertyValue = property.price || 0;
        const roi = propertyValue > 0 ? ((netIncome * 12) / propertyValue) * 100 : 0;

        return {
          id: property.id,
          title: property.title,
          totalUnits,
          occupiedUnits,
          occupancyRate,
          monthlyRevenue,
          maintenanceCosts,
          netIncome,
          roi: Math.round(roi * 100) / 100
        };
      }) || [];

      return {
        data: {
          properties: performanceData,
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Tenant Report Data
  async getTenantReport(): Promise<ApiResponse<{
    totalTenants: number;
    activeTenants: number;
    pendingTenants: number;
    paymentHistory: Array<{
      tenantName: string;
      lastPayment: string;
      amountDue: number;
      status: 'current' | 'late' | 'overdue';
    }>;
    demographics: {
      domestic: number;
      foreign: number;
    };
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      const { data: tenants, error: tenantsError } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          status,
          contracts(status, rent_amount, start_date, end_date),
          vouchers(amount, created_at, voucher_type)
        `)
        .eq('role', 'tenant');

      if (tenantsError) throw tenantsError;

      const totalTenants = tenants?.length || 0;
      const activeTenants = tenants?.filter(t => t.status === 'active').length || 0;
      const pendingTenants = tenants?.filter(t => t.status === 'pending').length || 0;

      const paymentHistory = tenants?.map(tenant => {
        const lastPayment = tenant.vouchers
          ?.filter(v => v.voucher_type === 'receipt')
          ?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

        const activeContract = tenant.contracts?.find(c => c.status === 'active');
        const amountDue = activeContract?.rent_amount || 0;

        // Determine payment status based on last payment date
        const daysSincePayment = lastPayment
          ? Math.floor((Date.now() - new Date(lastPayment.created_at).getTime()) / (1000 * 60 * 60 * 24))
          : 999;

        let status: 'current' | 'late' | 'overdue' = 'current';
        if (daysSincePayment > 35) status = 'overdue';
        else if (daysSincePayment > 30) status = 'late';

        return {
          tenantName: `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim(),
          lastPayment: lastPayment?.created_at || 'Never',
          amountDue,
          status
        };
      }) || [];

      // Demographics feature disabled - is_foreign column not in profiles table
      const demographics = {
        domestic: totalTenants,
        foreign: 0
      };

      return {
        data: {
          totalTenants,
          activeTenants,
          pendingTenants,
          paymentHistory,
          demographics,
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Maintenance Report Data
  async getMaintenanceReport(): Promise<ApiResponse<{
    totalRequests: number;
    pendingRequests: number;
    completedRequests: number;
    totalCosts: number;
    averageCost: number;
    requestsByPriority: { [key: string]: number };
    unpricedWorkOrders: number;
    costsByProperty: Array<{ propertyTitle: string; totalCost: number }>;
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      const { data: requests, error: requestsError } = await readReportRows<any>((from, to) => supabase
        .from('maintenance_requests')
        .select(`
          id,
          status,
          priority,
          property:properties(title),
          work_orders(actual_cost, estimated_cost)
        `).order('id').range(from, to));

      if (requestsError) throw requestsError;

      const totalRequests = requests?.length || 0;
      const pendingRequests = requests?.filter(r => r.status === 'pending').length || 0;
      const completedRequests = requests?.filter(r => r.status === 'completed').length || 0;

      // Calculate costs from work orders
      let totalCosts = 0;
      let unpricedWorkOrders = 0;
      const propertyData: { [key: string]: number } = {};
      const priorityData: { [key: string]: number } = {};

      requests?.forEach(request => {
        // Count by priority
        priorityData[request.priority] = (priorityData[request.priority] || 0) + 1;

        // Sum costs by property
        const workOrderCosts = request.work_orders?.reduce((sum, wo) => {
          if (wo.actual_cost == null) { unpricedWorkOrders += 1; return sum; }
          const cost = Number(wo.actual_cost);
          if (!Number.isFinite(cost)) throw new Error('Invalid maintenance cost');
          return sum + Math.round(cost * 100);
        }, 0) / 100 || 0;
        
        totalCosts += workOrderCosts;

        if (request.property?.title) {
          propertyData[request.property.title] = (propertyData[request.property.title] || 0) + workOrderCosts;
        }
      });

      const averageCost = totalRequests > 0 ? totalCosts / totalRequests : 0;

      const costsByProperty = Object.entries(propertyData).map(([propertyTitle, totalCost]) => ({
        propertyTitle,
        totalCost
      }));

      return {
        data: {
          totalRequests,
          pendingRequests,
          completedRequests,
          totalCosts,
          averageCost: Math.round(averageCost * 100) / 100,
          requestsByPriority: priorityData,
          unpricedWorkOrders,
          costsByProperty,
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Owner-specific Rental Income Report
  async getOwnerRentalIncomeReport(ownerId?: string, startDate?: string, endDate?: string): Promise<ApiResponse<{
    totalRentalIncome: number;
    monthlyBreakdown: Array<{ month: string; income: number; year: number }>;
    propertyBreakdown: Array<{ propertyId: string; propertyTitle: string; income: number }>;
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      const startDateFilter = startDate || new Date(new Date().getFullYear(), 0, 1).toISOString();
      const endDateFilter = endDate || new Date().toISOString();

      let query = supabase
        .from('vouchers')
        .select(`
          amount,
          created_at,
          property_id,
          property:properties(id, title, owner_id)
        `)
        .eq('voucher_type', 'receipt')
        .eq('status', 'posted')
        .gte('created_at', startDateFilter)
        .lte('created_at', endDateFilter);

      // Filter by owner if specified
      if (ownerId) {
        query = query.eq('property.owner_id', ownerId);
      }

      const { data: vouchers, error: vouchersError } = await query;

      if (vouchersError) throw vouchersError;

      const totalRentalIncome = vouchers?.reduce((sum, v) => sum + (v.amount || 0), 0) || 0;

      // Group by month and property
      const monthlyData: { [key: string]: number } = {};
      const propertyData: { [key: string]: { title: string; income: number } } = {};

      vouchers?.forEach(voucher => {
        const date = new Date(voucher.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + voucher.amount;

        if (voucher.property_id && voucher.property) {
          if (!propertyData[voucher.property_id]) {
            propertyData[voucher.property_id] = {
              title: voucher.property.title || 'Unknown Property',
              income: 0
            };
          }
          propertyData[voucher.property_id].income += voucher.amount;
        }
      });

      const monthlyBreakdown = Object.entries(monthlyData).map(([monthKey, income]) => {
        const [year, month] = monthKey.split('-');
        return {
          month: new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' }),
          year: parseInt(year),
          income
        };
      });

      const propertyBreakdown = Object.entries(propertyData).map(([propertyId, data]) => ({
        propertyId,
        propertyTitle: data.title,
        income: data.income
      }));

      return {
        data: {
          totalRentalIncome,
          monthlyBreakdown,
          propertyBreakdown,
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Owner-specific Maintenance Expenses Report
  async getOwnerMaintenanceExpensesReport(ownerId?: string, startDate?: string, endDate?: string): Promise<ApiResponse<{
    totalMaintenanceExpenses: number;
    monthlyBreakdown: Array<{ month: string; expenses: number; year: number }>;
    propertyBreakdown: Array<{ propertyId: string; propertyTitle: string; expenses: number }>;
    requestBreakdown: Array<{ requestId: string; title: string; cost: number; status: string }>;
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      const startDateFilter = startDate || new Date(new Date().getFullYear(), 0, 1).toISOString();
      const endDateFilter = endDate || new Date().toISOString();

      // Get maintenance-related payment vouchers
      let voucherQuery = supabase
        .from('vouchers')
        .select(`
          amount,
          created_at,
          property_id,
          description,
          property:properties(id, title, owner_id)
        `)
        .eq('voucher_type', 'payment')
        .eq('status', 'posted')
        .gte('created_at', startDateFilter)
        .lte('created_at', endDateFilter)
        .ilike('description', '%maintenance%');

      if (ownerId) {
        voucherQuery = voucherQuery.eq('property.owner_id', ownerId);
      }

      // Get work orders for maintenance requests
      const workOrderQuery = supabase
        .from('work_orders')
        .select(`
          actual_cost,
          estimated_cost,
          completion_date,
          description,
          status,
          maintenance_request:maintenance_requests(
            id,
            title,
            property:properties(id, title, owner_id)
          )
        `)
        .gte('completion_date', startDateFilter)
        .lte('completion_date', endDateFilter);

      const [vouchersResult, workOrdersResult] = await Promise.all([voucherQuery, workOrderQuery]);

      if (vouchersResult.error) throw vouchersResult.error;
      if (workOrdersResult.error) throw workOrdersResult.error;

      const vouchers = vouchersResult.data || [];
      const workOrders = (workOrdersResult.data || []).filter(wo => 
        !ownerId || wo.maintenance_request?.property?.owner_id === ownerId
      );

      // Combine voucher expenses and work order costs
      const totalVoucherExpenses = vouchers.reduce((sum, v) => sum + (v.amount || 0), 0);
      const totalWorkOrderExpenses = workOrders.reduce((sum, wo) => sum + (wo.actual_cost || wo.estimated_cost || 0), 0);
      const totalMaintenanceExpenses = totalVoucherExpenses + totalWorkOrderExpenses;

      // Group by month and property
      const monthlyData: { [key: string]: number } = {};
      const propertyData: { [key: string]: { title: string; expenses: number } } = {};
      const requestData: Array<{ requestId: string; title: string; cost: number; status: string }> = [];

      // Process vouchers
      vouchers.forEach(voucher => {
        const date = new Date(voucher.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + voucher.amount;

        if (voucher.property_id && voucher.property) {
          if (!propertyData[voucher.property_id]) {
            propertyData[voucher.property_id] = {
              title: voucher.property.title || 'Unknown Property',
              expenses: 0
            };
          }
          propertyData[voucher.property_id].expenses += voucher.amount;
        }
      });

      // Process work orders
      workOrders.forEach(workOrder => {
        const cost = workOrder.actual_cost || workOrder.estimated_cost || 0;
        const date = new Date(workOrder.completion_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + cost;

        const property = workOrder.maintenance_request?.property;
        if (property) {
          if (!propertyData[property.id]) {
            propertyData[property.id] = {
              title: property.title || 'Unknown Property',
              expenses: 0
            };
          }
          propertyData[property.id].expenses += cost;
        }

        // Add to request breakdown
        requestData.push({
          requestId: workOrder.maintenance_request?.id || '',
          title: workOrder.maintenance_request?.title || workOrder.description || 'Maintenance Work',
          cost,
          status: workOrder.status || 'completed'
        });
      });

      const monthlyBreakdown = Object.entries(monthlyData).map(([monthKey, expenses]) => {
        const [year, month] = monthKey.split('-');
        return {
          month: new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' }),
          year: parseInt(year),
          expenses
        };
      });

      const propertyBreakdown = Object.entries(propertyData).map(([propertyId, data]) => ({
        propertyId,
        propertyTitle: data.title,
        expenses: data.expenses
      }));

      return {
        data: {
          totalMaintenanceExpenses,
          monthlyBreakdown,
          propertyBreakdown,
          requestBreakdown: requestData,
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Manager-specific Cashflow Report (Income + Expenses combined)
  async getManagerCashflowReport(startDate?: string, endDate?: string): Promise<ApiResponse<{
    totalIncome: number;
    totalExpenses: number;
    netCashflow: number;
    monthlyBreakdown: Array<{ 
      month: string; 
      year: number;
      income: number; 
      expenses: number; 
      netCashflow: number; 
    }>;
    lastGenerated: string;
  }>> {
    return handleApiCall(async () => {
      const startDateFilter = startDate || new Date(new Date().getFullYear(), 0, 1).toISOString();
      const endDateFilter = endDate || new Date().toISOString();

      // Get all posted vouchers for the period
      const { data: vouchers, error: vouchersError } = await supabase
        .from('vouchers')
        .select(`
          amount,
          created_at,
          voucher_type
        `)
        .eq('status', 'posted')
        .gte('created_at', startDateFilter)
        .lte('created_at', endDateFilter);

      if (vouchersError) throw vouchersError;

      const incomeVouchers = vouchers?.filter(v => v.voucher_type === 'receipt') || [];
      const expenseVouchers = vouchers?.filter(v => v.voucher_type === 'payment') || [];

      const totalIncome = incomeVouchers.reduce((sum, v) => sum + (v.amount || 0), 0);
      const totalExpenses = expenseVouchers.reduce((sum, v) => sum + (v.amount || 0), 0);
      const netCashflow = totalIncome - totalExpenses;

      // Group by month
      const monthlyData: { [key: string]: { income: number; expenses: number } } = {};

      incomeVouchers.forEach(voucher => {
        const date = new Date(voucher.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { income: 0, expenses: 0 };
        }
        monthlyData[monthKey].income += voucher.amount;
      });

      expenseVouchers.forEach(voucher => {
        const date = new Date(voucher.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { income: 0, expenses: 0 };
        }
        monthlyData[monthKey].expenses += voucher.amount;
      });

      const monthlyBreakdown = Object.entries(monthlyData).map(([monthKey, data]) => {
        const [year, month] = monthKey.split('-');
        return {
          month: new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' }),
          year: parseInt(year),
          income: data.income,
          expenses: data.expenses,
          netCashflow: data.income - data.expenses
        };
      });

      return {
        data: {
          totalIncome,
          totalExpenses,
          netCashflow,
          monthlyBreakdown,
          lastGenerated: new Date().toISOString()
        },
        error: null
      };
    });
  },

  // Additional comprehensive reports as requested

  // Summary Reports
  async getSummaryReport(dateRange?: { startDate: string; endDate: string }): Promise<ApiResponse<any>> {
    try {
      const { data: properties } = await supabase.from('properties').select('*');
      const { data: contracts } = await supabase.from('contracts').select('*');
      const { data: vouchers } = await supabase.from('vouchers').select('*');
      const { data: maintenance } = await supabase.from('maintenance_requests').select('*');

      const summary = {
        totalProperties: properties?.length || 0,
        activeContracts: contracts?.filter(c => c.status === 'active').length || 0,
        totalRevenue: vouchers?.filter(v => v.voucher_type === 'receipt' && v.status === 'posted')
          .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
        totalExpenses: vouchers?.filter(v => v.voucher_type === 'payment' && v.status === 'posted')
          .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
        maintenanceRequests: maintenance?.length || 0,
        generatedAt: new Date().toISOString()
      };

      return { data: summary, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate summary report' };
    }
  },

  // Invoices Report  
  async getInvoicesReport(dateRange?: { startDate: string; endDate: string }): Promise<ApiResponse<any>> {
    try {
      let query = supabase
        .from('invoices')
        .select(`
          *,
          properties(title, address),
          profiles(first_name, last_name)
        `);

      if (dateRange?.startDate && dateRange?.endDate) {
        query = query
          .gte('issue_date', dateRange.startDate)
          .lte('issue_date', dateRange.endDate);
      }

      const { data: invoices, error } = await query;
      if (error) throw error;

      const summary = {
        totalInvoices: invoices?.length || 0,
        totalAmount: invoices?.reduce((sum, inv) => sum + Number(inv.total_amount), 0) || 0,
        paidInvoices: invoices?.filter(inv => inv.status === 'paid').length || 0,
        overdueInvoices: invoices?.filter(inv => inv.status === 'overdue').length || 0,
        invoices: invoices || [],
        generatedAt: new Date().toISOString()
      };

      return { data: summary, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate invoices report' };
    }
  },

  // Account Statement
  async getAccountStatement(accountId?: string, dateRange?: { startDate: string; endDate: string }): Promise<ApiResponse<any>> {
    try {
      const { data: vouchers, error } = await supabase
        .from('vouchers')
        .select(`
          *,
          accounts(account_code, account_name),
          properties(title),
          profiles(first_name, last_name)
        `)
        .eq('status', 'posted')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const statement = {
        openingBalance: 0,
        closingBalance: vouchers?.reduce((balance, v) => {
          return v.voucher_type === 'receipt' 
            ? balance + Number(v.amount)
            : balance - Number(v.amount);
        }, 0) || 0,
        transactions: vouchers || [],
        totalDebits: vouchers?.filter(v => v.voucher_type === 'payment')
          .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
        totalCredits: vouchers?.filter(v => v.voucher_type === 'receipt')
          .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
        generatedAt: new Date().toISOString()
      };

      return { data: statement, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate account statement' };
    }
  },

  // Financial Statements
  async getFinancialStatements(dateRange?: { startDate: string; endDate: string }): Promise<ApiResponse<any>> {
    try {
      const { data: vouchers } = await supabase
        .from('vouchers')
        .select('*, accounts(account_code, account_name, account_type)')
        .eq('status', 'posted');

      const { data: accounts } = await supabase
        .from('accounts')
        .select('*')
        .order('account_code');

      const revenue = vouchers?.filter(v => v.voucher_type === 'receipt')
        .reduce((sum, v) => sum + Number(v.amount), 0) || 0;
      
      const expenses = vouchers?.filter(v => v.voucher_type === 'payment')
        .reduce((sum, v) => sum + Number(v.amount), 0) || 0;

      const statements = {
        profitLoss: {
          revenue,
          expenses,
          netIncome: revenue - expenses
        },
        balanceSheet: {
          assets: accounts?.filter(a => a.account_type === 'asset') || [],
          liabilities: accounts?.filter(a => a.account_type === 'liability') || [],
          equity: accounts?.filter(a => a.account_type === 'equity') || []
        },
        cashFlow: {
          operatingActivities: revenue - expenses,
          investingActivities: 0,
          financingActivities: 0
        },
        generatedAt: new Date().toISOString()
      };

      return { data: statements, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate financial statements' };
    }
  },

  // Payments and Late Tenants Report
  async getPaymentsAndLateTenantsReport(): Promise<ApiResponse<any>> {
    try {
      const { data: contracts } = await supabase
        .from('contracts')
        .select(`
          *,
          properties(title, address),
          profiles(first_name, last_name, phone_number)
        `)
        .eq('status', 'active');

      const { data: vouchers } = await supabase
        .from('vouchers')
        .select('*')
        .eq('voucher_type', 'receipt')
        .eq('status', 'posted')
        .order('created_at', { ascending: false });

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const latePayments = contracts?.filter(contract => {
        const lastPayment = vouchers?.find(v => v.tenant_id === contract.tenant_id);
        const lastPaymentDate = lastPayment ? new Date(lastPayment.created_at) : new Date(contract.start_date);
        return lastPaymentDate < thirtyDaysAgo;
      }) || [];

      const report = {
        totalContracts: contracts?.length || 0,
        currentTenants: contracts?.length || 0,
        latePayments: latePayments.length,
        onTimePayments: (contracts?.length || 0) - latePayments.length,
        lateTenantsDetails: latePayments.map(contract => ({
          tenantName: `${contract.profiles?.first_name} ${contract.profiles?.last_name}`,
          propertyTitle: contract.properties?.title,
          rentAmount: contract.rent_amount,
          phone: contract.profiles?.phone_number,
          contractNumber: contract.contract_number,
          daysBehind: Math.floor((now.getTime() - new Date(contract.start_date).getTime()) / (1000 * 60 * 60 * 24))
        })),
        generatedAt: new Date().toISOString()
      };

      return { data: report, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate payments and late tenants report' };
    }
  },

  // Vacancies and Offers Report
  async getVacanciesAndOffersReport(): Promise<ApiResponse<any>> {
    try {
      const { data: properties } = await supabase
        .from('properties')
        .select(`
          *,
          profiles(first_name, last_name)
        `);

      const { data: bids } = await supabase
        .from('bids')
        .select(`
          *,
          properties(title, address),
          profiles(first_name, last_name)
        `)
        .order('created_at', { ascending: false });

      const vacantProperties = properties?.filter(p => p.status === 'available') || [];
      const reservedProperties = properties?.filter(p => p.status === 'reserved') || [];
      const activeBids = bids?.filter(b => b.bid_status === 'pending') || [];

      const report = {
        totalProperties: properties?.length || 0,
        vacantProperties: vacantProperties.length,
        reservedProperties: reservedProperties.length,
        occupiedProperties: properties?.filter(p => p.status === 'rented').length || 0,
        activeBids: activeBids.length,
        averageVacancyRate: ((vacantProperties.length / (properties?.length || 1)) * 100).toFixed(2),
        vacancyDetails: vacantProperties.map(p => ({
          title: p.title,
          address: p.address,
          price: p.price,
          propertyType: p.property_type,
          daysVacant: Math.floor((new Date().getTime() - new Date(p.updated_at).getTime()) / (1000 * 60 * 60 * 24))
        })),
        bidDetails: activeBids.map(b => ({
          propertyTitle: b.properties?.title,
          bidderName: `${b.profiles?.first_name} ${b.profiles?.last_name}`,
          bidAmount: b.bid_amount,
          bidType: b.bid_type,
          createdAt: b.created_at
        })),
        generatedAt: new Date().toISOString()
      };

      return { data: report, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate vacancies and offers report' };
    }
  },

  // Property Report
  async getPropertyReport(propertyId?: string): Promise<ApiResponse<any>> {
    try {
      let query = supabase
        .from('properties')
        .select(`
          *,
          profiles(first_name, last_name, phone_number),
          contracts(*, profiles(first_name, last_name)),
          maintenance_requests(*),
          vouchers(*)
        `);

      if (propertyId) {
        query = query.eq('id', propertyId);
      }

      const { data: properties, error } = await query;
      if (error) throw error;

      const report = properties?.map(property => ({
        propertyDetails: {
          title: property.title,
          address: property.address,
          propertyType: property.property_type,
          status: property.status,
          price: property.price,
          area: property.area_sqm,
          bedrooms: property.bedrooms,
          bathrooms: property.bathrooms
        },
        ownerDetails: {
          name: `${property.profiles?.first_name} ${property.profiles?.last_name}`,
          phone: property.profiles?.phone_number
        },
        financialSummary: {
          totalRevenue: property.vouchers?.filter(v => v.voucher_type === 'receipt')
            .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
          totalExpenses: property.vouchers?.filter(v => v.voucher_type === 'payment')
            .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
          maintenanceRequests: property.maintenance_requests?.length || 0
        },
        currentTenant: property.contracts?.find(c => c.status === 'active')?.profiles ? {
          name: `${property.contracts.find(c => c.status === 'active')?.profiles?.first_name} ${property.contracts.find(c => c.status === 'active')?.profiles?.last_name}`,
          rentAmount: property.contracts.find(c => c.status === 'active')?.rent_amount
        } : null
      })) || [];

      return { data: { properties: report, generatedAt: new Date().toISOString() }, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate property report' };
    }
  },

  // Tenants Statement Report
  async getTenantsStatementReport(ownerId?: string): Promise<ApiResponse<any>> {
    try {
      let propertiesQuery = supabase
        .from('properties')
        .select(`
          *,
          contracts!inner(*,
            profiles(first_name, last_name, phone_number)
          ),
          vouchers(*)
        `);

      if (ownerId) {
        propertiesQuery = propertiesQuery.eq('owner_id', ownerId);
      }

      const { data: properties, error } = await propertiesQuery;
      if (error) throw error;

      const tenantsStatement = properties?.map(property => {
        const activeContract = property.contracts?.find(c => c.status === 'active');
        const tenantPayments = property.vouchers?.filter(v => 
          v.voucher_type === 'receipt' && v.tenant_id === activeContract?.tenant_id
        ) || [];

        return {
          propertyTitle: property.title,
          tenantName: activeContract ?
            `${activeContract.profiles?.first_name} ${activeContract.profiles?.last_name}` : 'No Active Tenant',
          tenantPhone: activeContract?.profiles?.phone_number,
          rentAmount: activeContract?.rent_amount || 0,
          totalPaid: tenantPayments.reduce((sum, v) => sum + Number(v.amount), 0),
          lastPaymentDate: tenantPayments.length > 0 ? 
            Math.max(...tenantPayments.map(v => new Date(v.created_at).getTime())) : null,
          contractStatus: activeContract?.status || 'No Contract'
        };
      }) || [];

      return { 
        data: { 
          tenants: tenantsStatement, 
          generatedAt: new Date().toISOString() 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: 'Failed to generate tenants statement report' };
    }
  },

  // Owner Financial Report
  async getOwnerFinancialReport(ownerId?: string): Promise<ApiResponse<any>> {
    try {
      let query = supabase
        .from('profiles')
        .select(`
          *,
          properties(*,
            contracts(*),
            vouchers(*),
            maintenance_requests(*)
          )
        `)
        .eq('role', 'owner');

      if (ownerId) {
        query = query.eq('id', ownerId);
      }

      const { data: owners, error } = await query;
      if (error) throw error;

      const ownerReports = owners?.map(owner => {
        const totalRevenue = owner.properties?.flatMap(p => p.vouchers || [])
          .filter(v => v.voucher_type === 'receipt')
          .reduce((sum, v) => sum + Number(v.amount), 0) || 0;

        const totalExpenses = owner.properties?.flatMap(p => p.vouchers || [])
          .filter(v => v.voucher_type === 'payment')
          .reduce((sum, v) => sum + Number(v.amount), 0) || 0;

        const totalProperties = owner.properties?.length || 0;
        const occupiedProperties = owner.properties?.filter(p => p.status === 'rented').length || 0;

        return {
          ownerName: `${owner.first_name} ${owner.last_name}`,
          ownerPhone: owner.phone_number,
          totalProperties,
          occupiedProperties,
          vacantProperties: totalProperties - occupiedProperties,
          occupancyRate: totalProperties > 0 ? ((occupiedProperties / totalProperties) * 100).toFixed(2) : '0',
          totalRevenue,
          totalExpenses,
          netIncome: totalRevenue - totalExpenses,
          maintenanceRequests: owner.properties?.flatMap(p => p.maintenance_requests || []).length || 0
        };
      }) || [];

      return { 
        data: { 
          owners: ownerReports, 
          generatedAt: new Date().toISOString() 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: 'Failed to generate owner financial report' };
    }
  },

  // Owner Full Report
  async getOwnerFullReport(ownerId?: string): Promise<ApiResponse<any>> {
    try {
      let query = supabase
        .from('profiles')
        .select(`
          *,
          properties(*,
            contracts(*,
              profiles(first_name, last_name, phone_number)
            ),
            vouchers(*),
            maintenance_requests(*,
              work_orders(*)
            )
          )
        `)
        .eq('role', 'owner');

      if (ownerId) {
        query = query.eq('id', ownerId);
      }

      const { data: owners, error } = await query;
      if (error) throw error;

      const fullReports = owners?.map(owner => ({
        ownerInfo: {
          name: `${owner.first_name} ${owner.last_name}`,
          email: owner.email,
          phone: owner.phone_number,
          // Note: address, city, country columns don't exist in profiles table
          address: null,
          city: null,
          country: null
        },
        propertiesOverview: {
          totalProperties: owner.properties?.length || 0,
          rentedProperties: owner.properties?.filter(p => p.status === 'rented').length || 0,
          availableProperties: owner.properties?.filter(p => p.status === 'available').length || 0,
          maintenanceProperties: owner.properties?.filter(p => p.status === 'maintenance').length || 0
        },
        financialSummary: {
          totalRevenue: owner.properties?.flatMap(p => p.vouchers || [])
            .filter(v => v.voucher_type === 'receipt')
            .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
          totalExpenses: owner.properties?.flatMap(p => p.vouchers || [])
            .filter(v => v.voucher_type === 'payment')
            .reduce((sum, v) => sum + Number(v.amount), 0) || 0,
          maintenanceCosts: owner.properties?.flatMap(p => p.maintenance_requests || [])
            .flatMap(mr => mr.work_orders || [])
            .reduce((sum, wo) => sum + Number(wo.actual_cost || wo.estimated_cost || 0), 0) || 0
        },
        propertiesDetails: owner.properties?.map(property => ({
          title: property.title,
          address: property.address,
          status: property.status,
          price: property.price,
          currentTenant: property.contracts?.find(c => c.status === 'active')?.profiles ? 
            `${property.contracts.find(c => c.status === 'active')?.profiles?.first_name} ${property.contracts.find(c => c.status === 'active')?.profiles?.last_name}` : 'Vacant',
          monthlyRent: property.contracts?.find(c => c.status === 'active')?.rent_amount || 0,
          maintenanceRequests: property.maintenance_requests?.length || 0
        })) || [],
        generatedAt: new Date().toISOString()
      })) || [];

      return { data: { owners: fullReports }, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to generate owner full report' };
    }
  },

  // Electrical Meter Report (placeholder)
  async getElectricalMeterReport(): Promise<ApiResponse<any>> {
    try {
      const { data: utilityPayments } = await supabase
        .from('utility_payments')
        .select(`
          *,
          property:properties(id, title, address, city, owner_id,
            profiles:profiles!properties_owner_id_fkey(first_name, last_name, email, phone_number)
          )
        `)
        .eq('utility_type', 'electricity')
        .order('reading_date', { ascending: false });

      const meterReadings = utilityPayments?.map(payment => ({
        paymentId: payment.id,
        propertyId: payment.property_id,
        propertyTitle: payment.property?.title || 'Unknown Property',
        propertyAddress: payment.property?.address || '',
        propertyCity: payment.property?.city || '',
        ownerName: payment.property?.profiles ? 
          `${payment.property.profiles.first_name} ${payment.property.profiles.last_name}` : 
          'Unknown Owner',
        ownerEmail: payment.property?.profiles?.email || '',
        ownerPhone: payment.property?.profiles?.phone_number || '',
        meterNumber: payment.meter_number || 'N/A',
        currentReading: Number(payment.current_reading),
        previousReading: Number(payment.previous_reading),
        consumption: Number(payment.consumption),
        ratePerUnit: Number(payment.rate_per_unit),
        amount: Number(payment.amount),
        readingDate: payment.reading_date,
        dueDate: payment.due_date,
        paymentStatus: payment.payment_status,
        paymentDate: payment.payment_date,
        paymentMethod: payment.payment_method,
        paymentReference: payment.payment_reference,
        notes: payment.notes
      })) || [];

      // Calculate summary statistics
      const totalConsumption = meterReadings.reduce((sum, reading) => sum + reading.consumption, 0);
      const totalAmount = meterReadings.reduce((sum, reading) => sum + reading.amount, 0);
      const pendingAmount = meterReadings
        .filter(r => r.paymentStatus === 'pending')
        .reduce((sum, reading) => sum + reading.amount, 0);
      const overdueAmount = meterReadings
        .filter(r => r.paymentStatus === 'overdue')
        .reduce((sum, reading) => sum + reading.amount, 0);

      const statistics = {
        totalReadings: meterReadings.length,
        totalConsumption,
        averageConsumption: meterReadings.length > 0 ? totalConsumption / meterReadings.length : 0,
        totalAmount,
        pendingAmount,
        overdueAmount,
        paidAmount: totalAmount - pendingAmount - overdueAmount,
        paymentStatusBreakdown: {
          pending: meterReadings.filter(r => r.paymentStatus === 'pending').length,
          paid: meterReadings.filter(r => r.paymentStatus === 'paid').length,
          overdue: meterReadings.filter(r => r.paymentStatus === 'overdue').length
        }
      };

      return { 
        data: { 
          meterReadings,
          statistics,
          generatedAt: new Date().toISOString() 
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error generating electrical meter report:', error);
      return { data: null, error: 'Failed to generate electrical meter report' };
    }
  },

  // Owner Classes Report
  async getOwnerClassesReport(): Promise<ApiResponse<any>> {
    try {
      const { data: owners } = await supabase
        .from('profiles')
        .select(`
          *,
          properties(*)
        `)
        .eq('role', 'owner');

      const ownerClasses = owners?.map(owner => {
        const propertyCount = owner.properties?.length || 0;
        let classification = 'Small Owner';
        
        if (propertyCount >= 10) classification = 'Large Owner';
        else if (propertyCount >= 5) classification = 'Medium Owner';
        else if (propertyCount >= 2) classification = 'Regular Owner';

        return {
          ownerName: `${owner.first_name} ${owner.last_name}`,
          email: owner.email,
          phone: owner.phone_number,
          totalProperties: propertyCount,
          classification,
          nationality: 'Saudi', // nationality column not in profiles table
          joinDate: owner.created_at,
          totalPortfolioValue: owner.properties?.reduce((sum, p) => sum + Number(p.price || 0), 0) || 0
        };
      }) || [];

      const classificationSummary = {
        'Small Owner': ownerClasses.filter(o => o.classification === 'Small Owner').length,
        'Regular Owner': ownerClasses.filter(o => o.classification === 'Regular Owner').length,
        'Medium Owner': ownerClasses.filter(o => o.classification === 'Medium Owner').length,
        'Large Owner': ownerClasses.filter(o => o.classification === 'Large Owner').length
      };

      return { 
        data: { 
          owners: ownerClasses,
          classificationSummary,
          totalOwners: ownerClasses.length,
          generatedAt: new Date().toISOString() 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: 'Failed to generate owner classes report' };
    }
  },

  // Tenants Balance Report
  async getTenantsBalanceReport(): Promise<ApiResponse<any>> {
    try {
      const { data: tenants } = await supabase
        .from('profiles')
        .select(`
          *,
          contracts(*,
            properties(title, address)
          ),
          vouchers(*)
        `)
        .eq('role', 'tenant');

      const tenantBalances = tenants?.map(tenant => {
        const activeContract = tenant.contracts?.find(c => c.status === 'active');
        const payments = tenant.vouchers?.filter(v => v.voucher_type === 'receipt') || [];
        const totalPaid = payments.reduce((sum, v) => sum + Number(v.amount), 0);
        
        // Calculate expected payments based on contract duration
        const monthlyRent = activeContract?.rent_amount || 0;
        const contractStart = activeContract ? new Date(activeContract.start_date) : new Date();
        const monthsSinceStart = Math.max(1, Math.floor((new Date().getTime() - contractStart.getTime()) / (30 * 24 * 60 * 60 * 1000)));
        const expectedTotal = monthlyRent * monthsSinceStart;
        const balance = expectedTotal - totalPaid;

        return {
          tenantName: `${tenant.first_name} ${tenant.last_name}`,
          phone: tenant.phone_number,
          email: tenant.email,
          propertyTitle: activeContract?.properties?.title || 'No Active Property',
          monthlyRent,
          totalPaid,
          expectedTotal,
          balance,
          status: balance > 0 ? 'Outstanding' : 'Current',
          lastPaymentDate: payments.length > 0 ? 
            new Date(Math.max(...payments.map(p => new Date(p.created_at).getTime()))).toISOString().split('T')[0] : 'No payments',
          contractStatus: activeContract?.status || 'No Contract'
        };
      }) || [];

      const summary = {
        totalTenants: tenantBalances.length,
        currentTenants: tenantBalances.filter(t => t.status === 'Current').length,
        outstandingTenants: tenantBalances.filter(t => t.status === 'Outstanding').length,
        totalOutstanding: tenantBalances.filter(t => t.balance > 0).reduce((sum, t) => sum + t.balance, 0),
        totalPaid: tenantBalances.reduce((sum, t) => sum + t.totalPaid, 0)
      };

      return { 
        data: { 
          tenants: tenantBalances,
          summary,
          generatedAt: new Date().toISOString() 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: 'Failed to generate tenants balance report' };
    }
  },

  // Add missing filter methods for reports screen
  async getFilteredTenantReport(): Promise<ApiResponse<{ tenants: any[] }>> {
    return handleApiCall(async () => {
      const { data: tenants, error } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          status
        `)
        .eq('role', 'tenant')
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;

      return {
        data: { tenants: tenants || [] },
        error: null
      };
    });
  },

  async getFilteredOwnerFinancialReport(): Promise<ApiResponse<{ owners: any[] }>> {
    return handleApiCall(async () => {
      const { data: owners, error } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          status
        `)
        .eq('role', 'owner')
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;

      return {
        data: { owners: owners || [] },
        error: null
      };
    });
  },

  async getFilteredPropertyReport(): Promise<ApiResponse<{ properties: any[] }>> {
    return handleApiCall(async () => {
      const { data: properties, error } = await supabase
        .from('properties')
        .select(`
          id,
          title,
          address,
          city,
          property_type,
          status
        `)
        .order('title');

      if (error) throw error;

      return {
        data: { properties: properties || [] },
        error: null
      };
    });
  },

  // Water Meters Report Data
  async getWaterMetersReport(filters?: {
    dateRange?: {
      startDate: Date;
      endDate: Date;
      label: string;
    };
    propertyTypes?: string[];
    propertyIds?: string[];
    cities?: string[];
    neighborhoods?: string[];
    paymentStatuses?: string[];
    meterNumbers?: string[];
    minConsumption?: number;
    maxConsumption?: number;
    minAmount?: number;
    maxAmount?: number;
    includeSubProperties?: boolean;
  }): Promise<ApiResponse<{
    totalWaterMeters: number;
    totalConsumption: number;
    totalAmount: number;
    averageConsumption: number;
    monthlyBreakdown: Array<{ month: string; consumption: number; amount: number; year: number }>;
    propertyBreakdown: Array<{ propertyId: string; propertyTitle: string; consumption: number; amount: number; meterNumber: string; propertyType: string; city: string; neighborhood: string }>;
    paymentStatusBreakdown: { pending: number; paid: number; overdue: number };
    lastGenerated: string;
    appliedFilters: any;
  }>> {
    return handleApiCall(async () => {
      const startDateFilter = filters?.dateRange?.startDate?.toISOString() || new Date(new Date().getFullYear(), 0, 1).toISOString();
      const endDateFilter = filters?.dateRange?.endDate?.toISOString() || new Date().toISOString();

      validateReportAmountBounds(filters);
      const { data: waterPayments, error: waterError } = await readReportRows<any>((from, to) => {
      // Rebuild every page so bounds always precede pagination.
      let waterQuery = supabase
        .from('utility_payments')
        .select(`
          id,
          meter_number,
          previous_reading,
          current_reading,
          consumption,
          rate_per_unit,
          amount,
          reading_date,
          payment_status,
          property_id,
          property:properties(id, title, address, city, neighborhood, property_type)
        `)
        .eq('utility_type', 'water')
        .gte('reading_date', startDateFilter)
        .lte('reading_date', endDateFilter);

      // Apply property type filters
      if (filters?.propertyTypes && filters.propertyTypes.length > 0) {
        waterQuery = waterQuery.in('property.property_type', filters.propertyTypes);
      }

      // Apply property ID filters
      if (filters?.propertyIds && filters.propertyIds.length > 0) {
        waterQuery = waterQuery.in('property_id', filters.propertyIds);
      }

      // Apply city filters
      if (filters?.cities && filters.cities.length > 0) {
        waterQuery = waterQuery.in('property.city', filters.cities);
      }

      // Apply neighborhood filters
      if (filters?.neighborhoods && filters.neighborhoods.length > 0) {
        waterQuery = waterQuery.in('property.neighborhood', filters.neighborhoods);
      }

      // Apply payment status filters
      if (filters?.paymentStatuses && filters.paymentStatuses.length > 0) {
        waterQuery = waterQuery.in('payment_status', filters.paymentStatuses);
      }

      // Apply meter number filters
      if (filters?.meterNumbers && filters.meterNumbers.length > 0) {
        waterQuery = waterQuery.in('meter_number', filters.meterNumbers);
      }

      // Apply consumption range filters
      if (filters?.minConsumption !== undefined) {
        waterQuery = waterQuery.gte('consumption', filters.minConsumption);
      }
      if (filters?.maxConsumption !== undefined) {
        waterQuery = waterQuery.lte('consumption', filters.maxConsumption);
      }

      // Apply amount range filters
      if (filters?.minAmount !== undefined) {
        waterQuery = waterQuery.gte('amount', filters.minAmount);
      }
      if (filters?.maxAmount !== undefined) {
        waterQuery = waterQuery.lte('amount', filters.maxAmount);
      }

      // Apply sub-properties filter
      if (filters?.includeSubProperties === false) {
        waterQuery = waterQuery.eq('property.is_sub_property', false);
      }

      return waterQuery.order('id').range(from, to);
      });

      if (waterError) throw waterError;

      const totalWaterMeters = waterPayments?.length || 0;
      const totalConsumption = waterPayments.reduce((sum, w) => sum + Number(w.consumption || 0), 0);
      const totalAmount = waterPayments.reduce((sum, w) => sum + Math.round(Number(w.amount || 0) * 100), 0) / 100;
      const averageConsumption = totalWaterMeters > 0 ? totalConsumption / totalWaterMeters : 0;

      // Group by month
      const monthlyData: { [key: string]: { consumption: number; amount: number } } = {};
      const propertyData: { [key: string]: { title: string; consumption: number; amount: number; meterNumber: string } } = {};
      const paymentStatusData = { pending: 0, paid: 0, overdue: 0 };

      waterPayments?.forEach(payment => {
        const date = new Date(payment.reading_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { consumption: 0, amount: 0 };
        }
        monthlyData[monthKey].consumption += Number(payment.consumption || 0);
        monthlyData[monthKey].amount += Math.round(Number(payment.amount || 0) * 100);

        // Group by property
        if (payment.property_id && payment.property) {
          if (!propertyData[payment.property_id]) {
            propertyData[payment.property_id] = {
              title: payment.property.title || 'Unknown Property',
              consumption: 0,
              amount: 0,
              meterNumber: payment.meter_number || 'N/A',
              propertyType: payment.property.property_type || 'Unknown',
              city: payment.property.city || 'Unknown',
              neighborhood: payment.property.neighborhood || 'Unknown'
            };
          }
          propertyData[payment.property_id].consumption += Number(payment.consumption || 0);
          propertyData[payment.property_id].amount += Math.round(Number(payment.amount || 0) * 100);
        }

        // Count payment status
        switch (payment.payment_status) {
          case 'pending':
            paymentStatusData.pending++;
            break;
          case 'paid':
            paymentStatusData.paid++;
            break;
          case 'overdue':
            paymentStatusData.overdue++;
            break;
        }
      });

      const monthlyBreakdown = Object.entries(monthlyData).sort(([a], [b]) => a.localeCompare(b)).map(([monthKey, data]) => {
        const [year, month] = monthKey.split('-');
        return {
          month: new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' }),
          year: parseInt(year),
          consumption: data.consumption,
          monthNumber: parseInt(month),
          amount: data.amount / 100
        };
      });

      const propertyBreakdown = Object.entries(propertyData).map(([propertyId, data]) => ({
        propertyId,
        propertyTitle: data.title,
        consumption: data.consumption,
        amount: data.amount / 100,
        meterNumber: data.meterNumber,
        propertyType: data.propertyType,
        city: data.city,
        neighborhood: data.neighborhood
      }));

      return {
        data: {
          totalWaterMeters,
          totalConsumption,
          totalAmount,
          averageConsumption: Math.round(averageConsumption * 100) / 100,
          monthlyBreakdown,
          propertyBreakdown,
          paymentStatusBreakdown: paymentStatusData,
          lastGenerated: new Date().toISOString(),
          appliedFilters: filters || {}
        },
        error: null
      };
    });
  }
};

// Bidding API - Complete bidding system for tenants and buyers
export const bidsApi = {
  // Get all bids with filtering and role-based access
  async getAll(filters?: {
    property_id?: string;
    bidder_id?: string;
    bid_type?: 'rental' | 'purchase';
    bid_status?: string;
  }): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access bids' }
      };
    }

    let query = supabase
      .from('bids')
      .select(`
        *,
        property:properties(id, title, address, city, price, listing_type, owner_id),
        bidder:profiles!bids_bidder_id_fkey(id, first_name, last_name, email, phone_number),
        manager:profiles!bids_manager_id_fkey(id, first_name, last_name, email)
      `)
      .order('created_at', { ascending: false });

    // Role-based filtering
    if (userContext.role === 'tenant' || userContext.role === 'buyer') {
      // Users see only their own bids
      query = query.eq('bidder_id', userContext.userId);
    } else if (userContext.role === 'owner') {
      // Owners see bids on their properties
      query = query.eq('property.owner_id', userContext.userId);
    }
    // Managers/admins see all bids

    // Apply additional filters
    if (filters?.property_id) query = query.eq('property_id', filters.property_id);
    if (filters?.bidder_id) query = query.eq('bidder_id', filters.bidder_id);
    if (filters?.bid_type) query = query.eq('bid_type', filters.bid_type);
    if (filters?.bid_status) query = query.eq('bid_status', filters.bid_status);

    return handleApiCall(() => query);
  },

  // Get bids by property (for property owners and managers)
  async getByProperty(propertyId: string): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to access property bids' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('bids')
        .select(`
          *,
          property:properties(id, title, address, city, price, listing_type),
          bidder:profiles!bids_bidder_id_fkey(id, first_name, last_name, email, phone_number),
          manager:profiles!bids_manager_id_fkey(id, first_name, last_name, email)
        `)
        .eq('property_id', propertyId)
        .order('bid_amount', { ascending: false })
    );
  },

  // Create a new bid (for tenants and buyers)
  async create(bidData: {
    property_id: string;
    bid_type: 'rental' | 'purchase';
    bid_amount: number;
    message?: string;
    rental_duration_months?: number;
    security_deposit_amount?: number;
    utilities_included?: boolean;
    move_in_date?: string;
  }): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to submit bid' }
      };
    }

    // Validate user can bid
    if (!userContext.canBid) {
      return {
        data: null,
        error: { message: 'Your account needs approval before you can submit bids' }
      };
    }

    // Check if property accepts bids
    const { data: property } = await supabase
      .from('properties')
      .select('is_accepting_bids, minimum_bid_amount, maximum_bid_amount, listing_type, status')
      .eq('id', bidData.property_id)
      .single();

    if (!property?.is_accepting_bids || property.status !== 'available') {
      return {
        data: null,
        error: { message: 'This property is not currently accepting bids' }
      };
    }

    // Validate bid amount
    if (property.minimum_bid_amount && bidData.bid_amount < property.minimum_bid_amount) {
      return {
        data: null,
        error: { message: `Bid amount must be at least ${property.minimum_bid_amount} SAR` }
      };
    }

    if (property.maximum_bid_amount && bidData.bid_amount > property.maximum_bid_amount) {
      return {
        data: null,
        error: { message: `Bid amount cannot exceed ${property.maximum_bid_amount} SAR` }
      };
    }

    // Validate bid type matches property listing
    const requiredListingType = bidData.bid_type === 'purchase' ? 'sale' : 'rent';
    if (property.listing_type !== 'both' && property.listing_type !== requiredListingType) {
      return {
        data: null,
        error: { message: `This property is only available for ${property.listing_type}` }
      };
    }

    const bid = {
      property_id: bidData.property_id,
      bidder_id: userContext.userId,
      bid_type: bidData.bid_type,
      bid_amount: bidData.bid_amount,
      message: bidData.message,
      expires_at: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(), // 72 hours
      rental_duration_months: bidData.rental_duration_months,
      security_deposit_amount: bidData.security_deposit_amount,
      utilities_included: bidData.utilities_included,
      move_in_date: bidData.move_in_date
    };

    return handleApiCall(() => 
      supabase
        .from('bids')
        .insert(bid)
        .select(`
          *,
          property:properties(id, title, address, city, price),
          bidder:profiles!bids_bidder_id_fkey(id, first_name, last_name, email)
        `)
        .single()
    );
  },

  // Manager approval workflow
  async approveByManager(bidId: string, notes?: string): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || userContext.role !== 'manager') {
      return {
        data: null,
        error: { message: 'Only managers can approve bids' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('bids')
        .update({
          bid_status: 'manager_approved',
          manager_approved: true,
          manager_approval_date: new Date().toISOString(),
          manager_id: userContext.userId,
          manager_notes: notes
        })
        .eq('id', bidId)
        .eq('bid_status', 'pending')
        .select()
        .single()
    );
  },

  // Owner approval workflow
  async approveByOwner(bidId: string, notes?: string): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || userContext.role !== 'owner') {
      return {
        data: null,
        error: { message: 'Only property owners can approve bids' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('bids')
        .update({
          bid_status: 'accepted',
          owner_approved: true,
          owner_approval_date: new Date().toISOString(),
          owner_notes: notes
        })
        .eq('id', bidId)
        .eq('bid_status', 'manager_approved')
        .select()
        .single()
    );
  },

  // Reject bid
  async reject(bidId: string, reason: string, rejectedBy: 'manager' | 'owner'): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || (rejectedBy === 'manager' && userContext.role !== 'manager') || 
        (rejectedBy === 'owner' && userContext.role !== 'owner')) {
      return {
        data: null,
        error: { message: 'Insufficient permissions to reject bid' }
      };
    }

    const updateData = {
      bid_status: 'rejected',
      rejection_reason: reason,
      ...(rejectedBy === 'manager' && {
        manager_id: userContext.userId,
        manager_notes: reason,
        manager_approval_date: new Date().toISOString()
      }),
      ...(rejectedBy === 'owner' && {
        owner_notes: reason,
        owner_approval_date: new Date().toISOString()
      })
    };

    return handleApiCall(() => 
      supabase
        .from('bids')
        .update(updateData)
        .eq('id', bidId)
        .select()
        .single()
    );
  },

  // Withdraw bid (for bidders)
  async withdraw(bidId: string): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to withdraw bid' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('bids')
        .update({
          bid_status: 'withdrawn',
          updated_at: new Date().toISOString()
        })
        .eq('id', bidId)
        .eq('bidder_id', userContext.userId)
        .in('bid_status', ['pending', 'manager_approved'])
        .select()
        .single()
    );
  }
};

// User Approvals API - User signup and transaction approval workflow
export const userApprovalsApi = {
  async getActor() {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data?.user?.id) throw new Error('USER_MANAGEMENT_ACCESS_DENIED');
    const profile = await supabase.from('profiles').select('*').eq('id', data.user.id).single();
    if (profile.error) throw profile.error;
    const actor = profile.data;
    if (!actor || actor.id !== data.user.id || !['admin', 'manager'].includes(actor.role) ||
        actor.status !== 'active' || actor.approval_status !== 'approved' || actor.deleted_at) {
      throw new Error('USER_MANAGEMENT_ACCESS_DENIED');
    }
    return actor;
  },

  async getPendingUsers(): Promise<ApiResponse<any[]>> {
    const result = await userApprovalsApi.getAllUsers({ status: 'pending' });
    if (result.error) return result;
    return { data: result.data.filter(user => user.account_status === 'active' &&
      user.approval_status === 'pending' && !user.deleted_at), error: null };
  },

  async getAllUsers(filters?: { role?: string; status?: string; search?: string }): Promise<ApiResponse<any[]>> {
    try {
      const actor = await userApprovalsApi.getActor();
      const { normalizeProfileUser } = await import('./profileAccess');
      const rows = [];
      // Do not silently truncate the management list at PostgREST's row limit.
      for (let start = 0; ; start += 500) {
        let query = supabase.from('profiles').select('*').order('created_at', { ascending: false }).order('id').range(start, start + 499);
        if (filters?.role) query = query.eq('role', filters.role);
        const page = await query;
        if (page.error) throw page.error;
        if (!Array.isArray(page.data)) throw new Error('USER_MANAGEMENT_INVALID_RESPONSE');
        rows.push(...page.data);
        if (page.data.length < 500) break;
      }
      const current = await userApprovalsApi.getActor();
      if (current.id !== actor.id || current.role !== actor.role) throw new Error('USER_MANAGEMENT_SESSION_CHANGED');
      const search = filters?.search?.trim().toLocaleLowerCase();
      const data = rows.map(profile => ({ ...normalizeProfileUser(profile), phone: profile.phone_number || profile.phone || '' }))
        .filter(profile => (!filters?.status || profile.status === filters.status) &&
          (!search || [profile.first_name, profile.last_name, profile.email, profile.phone, profile.role]
            .some(value => String(value || '').toLocaleLowerCase().includes(search))));
      return { data, error: null };
    } catch (error) { return { data: null, error: error as Error }; }
  },

  async mutateUser(action: 'approve' | 'reject' | 'delete', userId: string, reason?: string, expectedActorId?: string): Promise<ApiResponse<any>> {
    try {
      if (!['approve', 'reject', 'delete'].includes(action)) throw new Error('USER_MANAGEMENT_INVALID_ACTION');
      const actor = await userApprovalsApi.getActor();
      if (!userId || userId === actor.id || (expectedActorId && expectedActorId !== actor.id)) throw new Error('USER_MANAGEMENT_ACCESS_DENIED');
      if (action === 'reject' && !reason?.trim()) throw new Error('USER_MANAGEMENT_REASON_REQUIRED');
      const before = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (before.error) throw before.error;
      const target = before.data;
      if (!target || target.id !== userId || target.deleted_at ||
          (actor.role === 'manager' && ['admin', 'manager'].includes(target.role)) ||
          (action !== 'delete' && (target.status !== 'active' || target.approval_status !== 'pending'))) {
        throw new Error('USER_MANAGEMENT_TARGET_DENIED');
      }
      const current = await userApprovalsApi.getActor();
      if (current.id !== actor.id || current.role !== actor.role) throw new Error('USER_MANAGEMENT_SESSION_CHANGED');
      const result = await supabase.rpc(action === 'approve' ? 'approve_user' : action === 'reject' ? 'reject_user' : 'soft_delete_user',
        action === 'delete' ? { user_id: userId, deleted_by: actor.id } :
          { user_id: userId, manager_id: actor.id, ...(action === 'reject' ? { reason: reason.trim() } : {}) });
      if (result.error) throw result.error;
      // These RPCs return void. An empty transport response is NOT evidence of success.
      const after = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (after.error) throw after.error;
      const saved = after.data;
      if (!saved || saved.id !== userId || (action === 'delete' ? saved.status !== 'inactive' || !saved.deleted_at :
          saved.status !== 'active' || saved.approval_status !== (action === 'approve' ? 'approved' : 'rejected') ||
          saved.deleted_at || saved.approved_by !== actor.id || !saved.approved_at ||
          (action === 'reject' && saved.rejected_reason !== reason.trim()))) {
        throw new Error('USER_MANAGEMENT_SAVE_UNCONFIRMED');
      }
      const finalActor = await userApprovalsApi.getActor();
      if (finalActor.id !== actor.id || finalActor.role !== actor.role) throw new Error('USER_MANAGEMENT_SESSION_CHANGED');
      const { normalizeProfileUser } = await import('./profileAccess');
      return { data: normalizeProfileUser(saved), error: null };
    } catch (error) { return { data: null, error: error as Error }; }
  },

  approveUser(userId: string, expectedActorId?: string): Promise<ApiResponse<any>> {
    return userApprovalsApi.mutateUser('approve', userId, undefined, expectedActorId);
  },
  rejectUser(userId: string, reason: string, expectedActorId?: string): Promise<ApiResponse<any>> {
    return userApprovalsApi.mutateUser('reject', userId, reason, expectedActorId);
  },
  deleteUser(userId: string, expectedActorId?: string): Promise<ApiResponse<any>> {
    return userApprovalsApi.mutateUser('delete', userId, undefined, expectedActorId);
  },

  async getUserApprovalHistory(userId: string): Promise<ApiResponse<any[]>> {
    try {
      const actor = await userApprovalsApi.getActor();
      const result = await supabase.from('user_approvals').select(`*, performed_by_profile:profiles!user_approvals_performed_by_fkey(first_name,last_name,email)`)
        .eq('user_id', userId).order('created_at', { ascending: false });
      if (result.error) throw result.error;
      if (!Array.isArray(result.data)) throw new Error('USER_MANAGEMENT_INVALID_RESPONSE');
      const current = await userApprovalsApi.getActor();
      if (current.id !== actor.id || current.role !== actor.role) throw new Error('USER_MANAGEMENT_SESSION_CHANGED');
      return result;
    } catch (error) { return { data: null, error: error as Error }; }
  },
  async getAuditLogs(entityType?: string, entityId?: string): Promise<ApiResponse<any[]>> {
    try {
      const actor = await userApprovalsApi.getActor();
      let query = supabase.from('audit_logs').select(`*, performed_by_profile:profiles!audit_logs_performed_by_fkey(first_name,last_name,email)`)
        .order('created_at', { ascending: false }).limit(100);
      if (entityType) query = query.eq('entity_type', entityType);
      if (entityId) query = query.eq('entity_id', entityId);
      const result = await query;
      if (result.error) throw result.error;
      if (!Array.isArray(result.data)) throw new Error('USER_MANAGEMENT_INVALID_RESPONSE');
      const current = await userApprovalsApi.getActor();
      if (current.id !== actor.id || current.role !== actor.role) throw new Error('USER_MANAGEMENT_SESSION_CHANGED');
      return result;
    } catch (error) { return { data: null, error: error as Error }; }
  }
};

// Tenant-specific API functions
export const tenantApi = {
  // Get available rental properties for tenants
  async getAvailableRentalProperties(filters?: {
    city?: string;
    property_type?: string;
    min_price?: number;
    max_price?: number;
    bedrooms?: number;
  }): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || userContext.role !== 'tenant') {
      return {
        data: null,
        error: { message: 'Only approved tenants can view rental properties' }
      };
    }

    let query = supabase
      .from('properties')
      .select(`
        *,
        owner:profiles!properties_owner_id_fkey(id, first_name, last_name, email, phone_number),
        existing_bids:bids(id, bid_amount, bid_status, bidder_id)
      `)
      .eq('status', 'available')
      .eq('is_accepting_bids', true)
      .in('listing_type', ['rent', 'both'])
      .eq('approval_status', 'approved')
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters?.city) query = query.ilike('city', `%${filters.city}%`);
    if (filters?.property_type) query = query.eq('property_type', filters.property_type);
    if (filters?.min_price) query = query.gte('price', filters.min_price);
    if (filters?.max_price) query = query.lte('price', filters.max_price);
    if (filters?.bedrooms) query = query.eq('bedrooms', filters.bedrooms);

    return handleApiCall(() => query);
  },

  // Get tenant's bids
  async getMyBids(): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || userContext.role !== 'tenant') {
      return {
        data: null,
        error: { message: 'Only tenants can view their bids' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('bids')
        .select(`
          *,
          property:properties(id, title, address, city, price, status, listing_type),
          manager:profiles!bids_manager_id_fkey(id, first_name, last_name, email)
        `)
        .eq('bidder_id', userContext.userId)
        .order('created_at', { ascending: false })
    );
  },

  // Get tenant's rental history and current contracts
  async getMyContracts(): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || userContext.role !== 'tenant') {
      return {
        data: null,
        error: { message: 'Only tenants can view their contracts' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('contracts')
        .select(`
          *,
          property:properties(id, title, address, city, neighborhood, property_type)
        `)
        .eq('tenant_id', userContext.userId)
        .order('start_date', { ascending: false })
    );
  },

  // Get tenant's maintenance requests
  async getMyMaintenanceRequests(): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || userContext.role !== 'tenant') {
      return {
        data: null,
        error: { message: 'Only tenants can view their maintenance requests' }
      };
    }

    return handleApiCall(() => 
      supabase
        .from('maintenance_requests')
        .select(`
          *,
          property:properties(id, title, address, city),
          work_orders(id, status, estimated_cost, actual_cost, completion_date)
        `)
        .eq('tenant_id', userContext.userId)
        .order('created_at', { ascending: false })
    );
  },

  // Submit maintenance request
  async createMaintenanceRequest(requestData: {
    property_id: string;
    title: string;
    description: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    images?: string[];
  }): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext || userContext.role !== 'tenant') {
      return {
        data: null,
        error: { message: 'Only tenants can submit maintenance requests' }
      };
    }

    // Verify tenant has active contract for this property
    const { data: contract } = await supabase
      .from('contracts')
      .select('id')
      .eq('tenant_id', userContext.userId)
      .eq('property_id', requestData.property_id)
      .eq('status', 'active')
      .single();

    if (!contract) {
      return {
        data: null,
        error: { message: 'You can only submit maintenance requests for properties you are currently renting' }
      };
    }

    const request = {
      property_id: requestData.property_id,
      tenant_id: userContext.userId,
      title: requestData.title,
      description: requestData.description,
      priority: requestData.priority || 'medium',
      images: requestData.images,
      status: 'pending'
    };

    return handleApiCall(() =>
      supabase
        .from('maintenance_requests')
        .insert(request)
        .select(`
          *,
          property:properties(id, title, address, city)
        `)
        .single()
    );
  },

  // Get tenant payment history
  async getPaymentHistory(tenantId: string): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();

    if (!userContext || userContext.role !== 'tenant' || userContext.userId !== tenantId) {
      return {
        data: null,
        error: { message: 'Access denied: Only tenants can view their payment history' }
      };
    }

    // Note: accounts table doesn't exist on remote
    return handleApiCall(() =>
      supabase
        .from('vouchers')
        .select(`
          *,
          property:properties(id, title, address, property_code)
        `)
        .eq('tenant_id', tenantId)
        .in('voucher_type', ['receipt', 'payment'])
        .order('created_at', { ascending: false })
    );
  },

  // Get tenant contracts with details
  async getContracts(tenantId: string): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();

    if (!userContext || userContext.role !== 'tenant' || userContext.userId !== tenantId) {
      return {
        data: null,
        error: { message: 'Access denied: Only tenants can view their contracts' }
      };
    }

    return handleApiCall(() =>
      supabase
        .from('contracts')
        .select(`
          *,
          property:properties(
            id,
            title,
            address,
            city,
            property_type,
            bedrooms,
            bathrooms,
            area_sqm,
            is_furnished,
            status
          )
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
    );
  }
};

// Bidding System API
export const biddingApi = {
  // Submit a bid on a property
  async submitBid(bidData: {
    property_id: string;
    bidder_id: string;
    bid_type: 'rental' | 'purchase';
    bid_amount: number;
    rental_duration_months?: number;
    security_deposit_amount?: number;
    utilities_included?: boolean;
    message?: string;
    expires_at: string;
  }): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await supabase
        .from('bids')
        .insert([{
          property_id: bidData.property_id,
          bidder_id: bidData.bidder_id,
          bid_type: bidData.bid_type,
          bid_amount: bidData.bid_amount,
          rental_duration_months: bidData.rental_duration_months,
          security_deposit_amount: bidData.security_deposit_amount,
          utilities_included: bidData.utilities_included,
          message: bidData.message,
          expires_at: bidData.expires_at,
          bid_status: 'pending'
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to submit bid' };
    }
  },

  // Get bids for a specific user
  async getMyBids(userId: string): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase
        .from('bids')
        .select(`
          *,
          property:properties(
            id, title, address, city, property_type, price, annual_rent, images,
            owner:profiles(id, first_name, last_name, phone_number)
          )
        `)
        .eq('bidder_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch bids' };
    }
  },

  // Get bids on properties owned by a specific user
  async getBidsOnMyProperties(ownerId: string): Promise<ApiResponse<any[]>> {
    try {
      const context = await getCurrentUserContext();
      if (!context || context.role !== 'owner' || context.userId !== ownerId) throw new Error('Access denied: Only your own property bids are available');
      const { data, error } = await supabase
        .from('bids')
        .select(`
          *,
          property:properties!inner(
            id, title, address, city, property_type, price, annual_rent
          ),
          bidder:profiles!bids_bidder_id_fkey(
            id, first_name, last_name, phone:phone_number, email
          )
        `)
        .eq('property.owner_id', ownerId)
        .in('bid_status', ['pending', 'manager_approved'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch property bids' };
    }
  },

  // Respond to a bid (accept/reject by property owner)
  async respondToBid(bidId: string, response: 'accept' | 'reject', ownerId: string, message?: string): Promise<ApiResponse<any>> {
    try {
      const context = await getCurrentUserContext();
      if (!context || context.role !== 'owner' || context.userId !== ownerId || !['accept', 'reject'].includes(response)) {
        throw new Error('Access denied: Only the property owner can respond to this bid');
      }
      const { data: bid, error: bidError } = await supabase.from('bids')
        .select('id, property_id, property:properties!inner(owner_id)')
        .eq('id', bidId)
        .eq('property.owner_id', context.userId)
        .eq('bid_status', 'manager_approved')
        .single();
      if (bidError || !bid) throw new Error(bidError?.message || 'Bid is unavailable for owner response');
      const updateData: any = {
        owner_approved: response === 'accept',
        owner_approval_date: new Date().toISOString(),
        owner_notes: message,
        ...(response === 'reject' && { rejection_reason: message }),
        bid_status: response === 'accept' ? 'owner_approved' : 'rejected'
      };

      const { data, error } = await supabase
        .from('bids')
        .update(updateData)
        .eq('id', bidId)
        .eq('property_id', bid.property_id)
        .eq('bid_status', 'manager_approved')
        .select()
        .single();

      if (error || !data) throw new Error(error?.message || 'Bid response was not saved');

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to respond to bid' };
    }
  },

  // Get properties available for bidding
  // Note: Bidding feature columns (minimum_bid_amount, maximum_bid_amount, is_accepting_bids, listing_expires_at, listing_type, approval_status) don't exist in properties table
  async getPropertiesForBidding(bidType: 'rental' | 'purchase', excludeOwnerId?: string): Promise<ApiResponse<any[]>> {
    try {
      let query = supabase
        .from('properties')
        .select(`
          id, title, description, property_type, address, city, country,
          price, annual_rent, bedrooms, bathrooms, area_sqm,
          images, is_furnished, status,
          owner:profiles!properties_owner_id_fkey(id, first_name, last_name, phone_number)
        `)
        .eq('status', 'available');

      if (excludeOwnerId) {
        query = query.neq('owner_id', excludeOwnerId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch properties for bidding' };
    }
  }
};

// Property Owner Maintenance API
export const ownerMaintenanceApi = {
  // Get maintenance requests for properties owned by a specific user
  async getMaintenanceRequestsForMyProperties(ownerId: string): Promise<ApiResponse<any[]>> {
    try {
      const context = await getCurrentUserContext();
      if (!context || context.role !== 'owner' || context.userId !== ownerId) throw new Error('Access denied: Only your own maintenance requests are available');
      const { data, error } = await supabase
        .from('maintenance_requests')
        .select(`
          *,
          property:properties!inner(
            id, title, address, city, owner_id
          ),
          tenant:profiles!maintenance_requests_tenant_id_fkey(
            id, first_name, last_name, phone:phone_number, email
          ),
          work_orders(
            id, status, estimated_cost, actual_cost, start_date, completion_date,
            assigned_to:profiles(id, first_name, last_name, phone_number)
          )
        `)
        .eq('property.owner_id', ownerId)
        .in('status', ['pending', 'approved', 'in_progress'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch maintenance requests' };
    }
  },

  // Create work order for maintenance request
  async createWorkOrder(workOrderData: {
    maintenance_request_id: string;
    assigned_to?: string;
    description: string;
    estimated_cost: number;
    start_date: string;
  }): Promise<ApiResponse<any>> {
    try {
      const context = await getCurrentUserContext();
      if (!context || !['admin', 'manager'].includes(context.role)) throw new Error('Access denied: Work orders require admin or manager');
      const { data: workOrder, error: workOrderError } = await supabase
        .from('work_orders')
        .insert([{
          maintenance_request_id: workOrderData.maintenance_request_id,
          assigned_to: workOrderData.assigned_to,
          description: workOrderData.description,
          estimated_cost: workOrderData.estimated_cost,
          start_date: workOrderData.start_date,
          status: 'assigned'
        }])
        .select()
        .single();

      if (workOrderError) throw workOrderError;

      // Update maintenance request status
      const { error: updateError } = await supabase
        .from('maintenance_requests')
        .update({ status: 'approved' })
        .eq('id', workOrderData.maintenance_request_id);

      if (updateError) throw updateError;

      return { success: true, data: workOrder };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create work order' };
    }
  },

  // Get contractors/employees for work order assignment
  async getContractors(): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, phone, email')
        .in('role', ['employee', 'contractor'])
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch contractors' };
    }
  }
};

// Property Management API for Owners
export const ownerPropertyApi = {
  // Get properties owned by a specific user
  async getMyProperties(ownerId: string): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          contracts(
            id, tenant_id, start_date, end_date, rent_amount, payment_frequency, status,
            tenant:profiles(id, first_name, last_name, phone_number)
          )
        `)
        .eq('owner_id', ownerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch properties' };
    }
  },

  // Create new property listing (requires manager approval)
  async createProperty(propertyData: {
    title: string;
    description: string;
    property_type: string;
    address: string;
    city: string;
    country: string;
    area_sqm: number;
    bedrooms?: number;
    bathrooms?: number;
    price: number;
    annual_rent?: number;
    owner_id: string;
    listing_type: 'rent' | 'sale' | 'both';
    minimum_bid_amount?: number;
    maximum_bid_amount?: number;
    bid_increment?: number;
    listing_expires_at: string;
    amenities?: string[];
    is_furnished?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      // Create property with pending approval status
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .insert([{
          ...propertyData,
          approval_status: 'pending',
          is_accepting_bids: false, // Will be enabled after approval
          status: 'available'
        }])
        .select()
        .single();

      if (propertyError) throw propertyError;

      // Create approval request
      const { error: approvalError } = await supabase
        .from('user_approvals')
        .insert([{
          approval_type: 'property_listing',
          requested_by: propertyData.owner_id,
          related_entity_type: 'property',
          related_entity_id: property.id,
          expires_at: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString() // 3 days
        }]);

      if (approvalError) throw approvalError;

      return { success: true, data: property };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create property' };
    }
  },

  // Get property analytics for owner
  async getPropertyAnalytics(propertyId: string, ownerId: string): Promise<ApiResponse<any>> {
    try {
      // Get property details
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', propertyId)
        .eq('owner_id', ownerId)
        .single();

      if (propertyError) throw propertyError;

      // Get maintenance costs
      const { data: maintenanceCosts, error: maintenanceError } = await supabase
        .from('work_orders')
        .select('actual_cost, estimated_cost')
        .eq('maintenance_request.property_id', propertyId);

      // Get rental income
      const { data: contracts, error: contractsError } = await supabase
        .from('contracts')
        .select('rent_amount, start_date, end_date, status')
        .eq('property_id', propertyId);

      // Get bids
      const { data: bids, error: bidsError } = await supabase
        .from('bids')
        .select('bid_amount, bid_type, bid_status, created_at')
        .eq('property_id', propertyId);

      const totalMaintenanceCosts = maintenanceCosts?.reduce((sum, order) => 
        sum + (order.actual_cost || order.estimated_cost || 0), 0) || 0;

      const monthlyRent = contracts?.find(c => c.status === 'active')?.rent_amount || 0;
      const annualRent = monthlyRent * 12;
      const roi = property.price > 0 ? ((annualRent - totalMaintenanceCosts) / property.price) * 100 : 0;

      const analytics = {
        property,
        monthlyRent,
        annualRent,
        totalMaintenanceCosts,
        roi,
        activeBids: bids?.filter(b => b.bid_status === 'pending').length || 0,
        totalBids: bids?.length || 0,
        occupancyStatus: contracts?.some(c => c.status === 'active') ? 'occupied' : 'vacant'
      };

      return { success: true, data: analytics };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch property analytics' };
    }
  }
};

// User Approval API (for managers)
export const approvalsApi = {
  // Get pending user approvals
  async getPendingUserApprovals(): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase
        .from('user_approvals')
        .select(`
          *,
          profile:profiles(
            id, first_name, last_name, email, phone, role, kyc_status
          )
        `)
        .eq('approval_status', 'pending')
        .eq('approval_type', 'user_signup')
        .order('created_at');

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch pending approvals' };
    }
  },

  // Get pending property approvals
  async getPendingPropertyApprovals(): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase
        .from('user_approvals')
        .select(`
          *,
          property:properties(
            id, title, address, city, property_type, price, listing_type,
            owner:profiles(id, first_name, last_name)
          )
        `)
        .eq('approval_status', 'pending')
        .eq('approval_type', 'property_listing')
        .order('created_at');

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch pending property approvals' };
    }
  },

  // Approve user
  async approveUser(userId: string, managerId: string, notes?: string): Promise<ApiResponse<any>> {
    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          approval_status: 'approved',
          approved_by: managerId,
          approval_date: new Date().toISOString(),
          can_bid: true,
          kyc_status: 'verified'
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      // Update approval record
      const { data, error: approvalError } = await supabase
        .from('user_approvals')
        .update({
          approval_status: 'approved',
          approved_by: managerId,
          approval_date: new Date().toISOString(),
          approval_notes: notes
        })
        .eq('related_entity_id', userId)
        .eq('approval_type', 'user_signup')
        .select()
        .single();

      if (approvalError) throw approvalError;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to approve user' };
    }
  },

  // Approve property
  async approveProperty(propertyId: string, managerId: string): Promise<ApiResponse<any>> {
    try {
      // Update property
      const { error: propertyError } = await supabase
        .from('properties')
        .update({
          approval_status: 'approved',
          approved_by: managerId,
          approval_date: new Date().toISOString(),
          is_accepting_bids: true
        })
        .eq('id', propertyId);

      if (propertyError) throw propertyError;

      // Update approval record
      const { data, error: approvalError } = await supabase
        .from('user_approvals')
        .update({
          approval_status: 'approved',
          approved_by: managerId,
          approval_date: new Date().toISOString()
        })
        .eq('related_entity_id', propertyId)
        .eq('approval_type', 'property_listing')
        .select()
        .single();

      if (approvalError) throw approvalError;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to approve property' };
    }
  }
};

// Buyer API (comprehensive buyer functionality)
export const buyerApi = {
  // Marketplace details never load the private management record graph.
  async getPropertyDetails(propertyId: string): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      const context = await getCurrentUserContext();
      if (!context?.isAuthenticated || !context.userId?.trim() || !['buyer', 'owner'].includes(context.role)) throw new Error('Access denied: Marketplace access required');
      if (typeof propertyId !== 'string' || !propertyId.trim()) throw new Error('Property ID is required');
      const result = await supabase.from('properties').select(`
        id, title, description, property_type, address, city, country, price, annual_rent,
        bedrooms, bathrooms, area_sqm, images, is_furnished, status,
        owner_id, listing_type, listing_expires_at, is_accepting_bids, approval_status,
        minimum_bid_amount, maximum_bid_amount, bid_increment,
        owner:profiles!properties_owner_id_fkey(id, first_name, last_name, phone_number)
      `)
        .eq('id', propertyId)
        .eq('status', 'available')
        .eq('approval_status', 'approved')
        .eq('is_accepting_bids', true)
        .or(`listing_expires_at.is.null,listing_expires_at.gt.${new Date().toISOString()}`)
        .single();
      if (result.error) return result;
      if (!result.data || result.data.id !== propertyId) throw new Error('Property is unavailable');
      const current = await getCurrentUserContext();
      if (!current?.isAuthenticated || current.userId !== context.userId || current.role !== context.role) throw new Error('Marketplace session changed');
      return result;
    });
  },

  // Get properties available for purchase
  async getBrowseProperties(filters?: {
    city?: string;
    property_type?: string;
    min_price?: number;
    max_price?: number;
    bedrooms?: number;
    min_bedrooms?: number;
    listing_type?: 'sale' | 'rent' | 'both';
  }): Promise<LegacyApiResponse<any[]>> {
    try {
      const context = await getCurrentUserContext();
      if (!context?.isAuthenticated || !context.userId?.trim() || !['buyer', 'owner'].includes(context.role)) throw new Error('Access denied: Marketplace access required');
      for (const key of ['min_price', 'max_price', 'bedrooms', 'min_bedrooms'] as const) {
        const value = filters?.[key];
        if (value !== undefined && (!Number.isFinite(value) || value < 0 ||
          (key.includes('bedrooms') && !Number.isInteger(value)))) throw new Error('Invalid marketplace filters');
      }
      if (filters?.min_price !== undefined && filters?.max_price !== undefined && filters.min_price > filters.max_price) throw new Error('Invalid marketplace price range');
      let query = supabase
        .from('properties')
        .select(`
          id, title, description, property_type, address, city, country, price, annual_rent,
          bedrooms, bathrooms, area_sqm, images, is_furnished, status,
          owner_id, listing_type, listing_expires_at, is_accepting_bids, approval_status,
          minimum_bid_amount, maximum_bid_amount, bid_increment,
          owner:profiles!properties_owner_id_fkey(id, first_name, last_name, phone_number)
        `)
        .eq('status', 'available')
        .eq('approval_status', 'approved')
        .eq('is_accepting_bids', true)
        .or(`listing_expires_at.is.null,listing_expires_at.gt.${new Date().toISOString()}`);

      // Apply filters
      if (filters?.city) {
        query = query.ilike('city', `%${filters.city}%`);
      }
      if (filters?.property_type) {
        query = query.eq('property_type', filters.property_type);
      }
      if (filters?.min_price !== undefined) {
        query = query.gte('price', filters.min_price);
      }
      if (filters?.max_price !== undefined) {
        query = query.lte('price', filters.max_price);
      }
      if (filters?.bedrooms !== undefined) {
        query = query.eq('bedrooms', filters.bedrooms);
      }
      if (filters?.min_bedrooms !== undefined) query = query.gte('bedrooms', filters.min_bedrooms);
      if (filters?.listing_type && filters.listing_type !== 'both') {
        query = query.in('listing_type', [filters.listing_type, 'both']);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      if (!Array.isArray(data)) throw new Error('Marketplace response is missing property rows');
      const current = await getCurrentUserContext();
      if (!current?.isAuthenticated || current.userId !== context.userId || current.role !== context.role) throw new Error('Marketplace session changed');
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error && typeof error === 'object' && typeof error.message === 'string'
        ? error.message : 'Failed to fetch properties' };
    }
  },

  // Get buyer's bid history
  async getMyBids(buyerId: string): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase
        .from('bids')
        .select(`
          *,
          property:properties(
            id, title, address, city, price, images,
            owner:profiles(id, first_name, last_name, phone_number)
          )
        `)
        .eq('bidder_id', buyerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch bids' };
    }
  },

  // Submit a new bid
  async submitBid(bidData: {
    property_id: string;
    bidder_id: string;
    bid_type: 'purchase' | 'rental';
    bid_amount: number;
    message?: string;
    rental_duration_months?: number;
    security_deposit_amount?: number;
    utilities_included?: boolean;
    move_in_date?: string;
  }): Promise<ApiResponse<any>> {
    try {
      // Check if user can bid
      const { data: profile } = await supabase
        .from('profiles')
        .select('can_bid, approval_status')
        .eq('id', bidData.bidder_id)
        .single();

      if (!profile?.can_bid || profile?.approval_status !== 'approved') {
        throw new Error('User is not approved to submit bids');
      }

      // Check if property is available for bidding
      const { data: property } = await supabase
        .from('properties')
        .select('is_accepting_bids, owner_id, minimum_bid_amount, maximum_bid_amount')
        .eq('id', bidData.property_id)
        .single();

      if (!property?.is_accepting_bids) {
        throw new Error('Property is not accepting bids');
      }

      if (property.owner_id === bidData.bidder_id) {
        throw new Error('Cannot bid on your own property');
      }

      // Validate bid amount
      if (property.minimum_bid_amount && bidData.bid_amount < property.minimum_bid_amount) {
        throw new Error(`Bid amount must be at least ${property.minimum_bid_amount}`);
      }

      if (property.maximum_bid_amount && bidData.bid_amount > property.maximum_bid_amount) {
        throw new Error(`Bid amount cannot exceed ${property.maximum_bid_amount}`);
      }

      // Check for existing active bid
      const { data: existingBids, error: existingBidError } = await supabase
        .from('bids')
        .select('id')
        .eq('property_id', bidData.property_id)
        .eq('bidder_id', bidData.bidder_id)
        .in('bid_status', ['pending', 'manager_approved', 'owner_approved', 'accepted'])
        .or(`expires_at.gt.${new Date().toISOString()},bid_status.in.(owner_approved,accepted)`)
        .limit(1);

      if (existingBidError) throw existingBidError;
      if (existingBids?.length) {
        throw new Error('You already have an active bid on this property');
      }

      // Create the bid
      const { data: bid, error } = await supabase
        .from('bids')
        .insert([{
          property_id: bidData.property_id,
          bidder_id: bidData.bidder_id,
          bid_type: bidData.bid_type,
          bid_amount: bidData.bid_amount,
          message: bidData.message,
          rental_duration_months: bidData.rental_duration_months,
          security_deposit_amount: bidData.security_deposit_amount,
          utilities_included: bidData.utilities_included,
          move_in_date: bidData.move_in_date,
          expires_at: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(), // 72 hours
          bid_status: 'pending'
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: bid };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to submit bid' };
    }
  },

  // Get buyer dashboard data
  async getDashboardData(buyerId: string): Promise<ApiResponse<{
    totalBids: number;
    pendingBids: number;
    acceptedBids: number;
    rejectedBids: number;
    availableProperties: number;
    approvalStatus: string;
    canBid: boolean;
  }>> {
    try {
      // Get bid statistics
      const context = await getCurrentUserContext();
      if (!context || context.role !== 'buyer' || context.userId !== buyerId) throw new Error('Access denied');
      const { data: bids, error: bidsError } = await supabase
        .from('bids')
        .select('bid_status')
        .eq('bidder_id', buyerId);
      if (bidsError) throw bidsError;
      if (!bids) throw new Error('Bid statistics unavailable');

      const bidStats = bids?.reduce((acc, bid) => {
        acc.total++;
        if (bid.bid_status === 'pending' || bid.bid_status === 'manager_approved') acc.pending++;
        else if (bid.bid_status === 'owner_approved' || bid.bid_status === 'accepted') acc.accepted++;
        else if (bid.bid_status === 'rejected') acc.rejected++;
        return acc;
      }, { total: 0, pending: 0, accepted: 0, rejected: 0 }) || { total: 0, pending: 0, accepted: 0, rejected: 0 };

      // Get available properties count
      const { count: availableCount, error: countError } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'available')
        .eq('approval_status', 'approved')
        .eq('is_accepting_bids', true)
        .or(`listing_expires_at.is.null,listing_expires_at.gt.${new Date().toISOString()}`);
      if (countError) throw countError;
      if (availableCount == null) throw new Error('Listing count unavailable');

      // Get buyer profile info
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('approval_status, can_bid')
        .eq('id', buyerId)
        .single();
      if (profileError) throw profileError;
      if (!profile) throw new Error('Buyer profile unavailable');

      const dashboardData = {
        totalBids: bidStats.total,
        pendingBids: bidStats.pending,
        acceptedBids: bidStats.accepted,
        rejectedBids: bidStats.rejected,
        availableProperties: availableCount || 0,
        approvalStatus: profile?.approval_status || 'pending',
        canBid: profile?.can_bid || false
      };

      return { success: true, data: dashboardData };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch dashboard data' };
    }
  },

  // Withdraw a bid
  async withdrawBid(bidId: string, buyerId: string): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      const { data, error } = await supabase
        .from('bids')
        .update({
          bid_status: 'withdrawn',
          withdrawal_date: new Date().toISOString(),
          withdrawal_reason: 'Withdrawn by buyer'
        })
        .eq('id', bidId)
        .eq('bidder_id', buyerId)
        .eq('bid_status', 'pending')
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    });
  }
};

// Notifications API
export const notificationsApi = {
  // Get all notifications for current user
  async getAll(filters?: {
    is_read?: boolean;
    notification_type?: string;
    priority?: string;
  }): Promise<ApiResponse<any[]>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to fetch notifications' }
      };
    }

    let query = supabase
      .from('notifications')
      .select(`
        *,
        sender:profiles!notifications_sender_id_fkey(id, first_name, last_name, email)
      `)
      .eq('recipient_id', userContext.userId)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters?.is_read !== undefined) query = query.eq('is_read', filters.is_read);
    if (filters?.notification_type) query = query.eq('notification_type', filters.notification_type);
    if (filters?.priority) query = query.eq('priority', filters.priority);

    return handleApiCall(async () => {
      const result = await query;
      return { data: result.data, error: result.error, count: result.count };
    });
  },

  // Get unread notifications count
  async getUnreadCount(): Promise<ApiResponse<number>> {
    return handleApiCall(async () => {
      const context = await getCurrentUserContext();
      if (!context) throw new Error('Authentication required to count notifications');
      const result = await supabase.from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', context.userId)
        .eq('is_read', false);
      if (result.error) return { data: null, error: result.error };
      if (result.count === null || result.count === undefined) throw new Error('Notification count unavailable');
      return { data: result.count, error: null };
    });
  },

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to update notification' }
      };
    }

    return handleApiCall(async () => {
      const result = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('recipient_id', userContext.userId)
        .select()
        .single();
      return { data: result.data, error: result.error };
    });
  },

  // Mark all notifications as read
  async markAllAsRead(): Promise<ApiResponse<any>> {
    const userContext = await getCurrentUserContext();
    
    if (!userContext) {
      return {
        data: null,
        error: { message: 'Authentication required to update notifications' }
      };
    }

    return handleApiCall(async () => {
      const result = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('recipient_id', userContext.userId)
        .eq('is_read', false);
      return { data: result.data, error: result.error };
    });
  },

  // Create notification
  async create(notification: {
    recipient_id: string;
    sender_id?: string;
    notification_type: string;
    title: string;
    message: string;
    priority?: 'low' | 'medium' | 'high';
    related_entity_type?: string;
    related_entity_id?: string;
  }): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      const context = await getCurrentUserContext();
      if (!context || !['admin', 'manager'].includes(context.role)) throw new Error('Access denied: Only admins and managers can send notifications');
      const result = await supabase
        .from('notifications')
        .insert({
          ...notification,
          sender_id: context.userId,
          is_read: false,
          created_at: new Date().toISOString()
        });
      // Recipient-only SELECT policies must not turn a successful send into an error.
      return { data: null, error: result.error };
    });
  }
};

// Add these interfaces after the existing interfaces
interface ReportFilters {
  tenantId?: string;
  ownerId?: string;
  propertyId?: string;
  reportType?: string;
  startDate?: string;
  endDate?: string;
}

interface UserContext {
  userId: string;
  role: 'admin' | 'manager' | 'owner' | 'tenant';
  ownedPropertyIds?: string[];
}



// Enhanced Reports API with role-based access control
export const enhancedReportsApi = {
  // Enhanced Revenue Report with role-based filtering
  async getRevenueReport(userContext: UserContext, filters: ReportFilters = {}): Promise<ApiResponse<any>> {
    try {
      let query = supabase
        .from('vouchers')
        .select(`
          *,
          property:properties(title, owner_id)
        `)
        .eq('voucher_type', 'receipt')
        .eq('status', 'posted');

      // Apply role-based filtering
      if (userContext.role === 'owner') {
        if (userContext.ownedPropertyIds) {
          query = query.in('property_id', userContext.ownedPropertyIds);
        } else {
          return { success: true, data: { revenue: [], summary: { total: 0, byProperty: {} } } };
        }
      }

      if (filters.ownerId && userContext.role === 'manager') {
        const { data: ownerProperties } = await supabase
          .from('properties')
          .select('id')
          .eq('owner_id', filters.ownerId);
        
        if (ownerProperties) {
          query = query.in('property_id', ownerProperties.map(p => p.id));
        }
      }

      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      const { data: revenue, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const total = revenue?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0;
      const byProperty = revenue?.reduce((acc, r) => {
        const propertyTitle = r.property?.title || 'Unknown Property';
        acc[propertyTitle] = (acc[propertyTitle] || 0) + (r.amount || 0);
        return acc;
      }, {} as Record<string, number>) || {};

      return {
        success: true,
        data: {
          revenue: revenue || [],
          summary: { total, byProperty },
          lastGenerated: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get revenue report' };
    }
  },

  // Enhanced Property Report for single property
  async getPropertyReport(userContext: UserContext, filters: ReportFilters): Promise<ApiResponse<any>> {
    try {
      if (!filters.propertyId) {
        return { success: false, error: 'Property ID is required' };
      }

      // Check if user has access to this property
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select(`
          *,
          owner:profiles!owner_id(first_name, last_name, email),
          contracts(*, tenant:profiles!tenant_id(first_name, last_name)),
          maintenance_requests(*, work_orders(*))
        `)
        .eq('id', filters.propertyId)
        .single();

      if (propertyError) throw propertyError;

      // Role-based access control
      if (userContext.role === 'owner' && property.owner_id !== userContext.userId) {
        return { success: false, error: 'Access denied: Not your property' };
      }

      // Get financial data for this property
      const { data: vouchers } = await supabase
        .from('vouchers')
        .select('*')
        .eq('property_id', filters.propertyId);

      const revenue = vouchers?.filter(v => v.voucher_type === 'receipt' && v.status === 'posted')
        .reduce((sum, v) => sum + (v.amount || 0), 0) || 0;

      const expenses = vouchers?.filter(v => v.voucher_type === 'payment' && v.status === 'posted')
        .reduce((sum, v) => sum + (v.amount || 0), 0) || 0;

      return {
        success: true,
        data: {
          property,
          financials: { revenue, expenses, netIncome: revenue - expenses },
          contracts: property.contracts || [],
          maintenance: property.maintenance_requests || [],
          lastGenerated: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get property report' };
    }
  },

  // Enhanced Tenant Statement with role-based filtering
  async getTenantStatement(userContext: UserContext, filters: ReportFilters): Promise<ApiResponse<any>> {
    try {
      if (!filters.tenantId) {
        return { success: false, error: 'Tenant ID is required' };
      }

      // Get tenant contracts and check access
      const { data: contracts, error: contractsError } = await supabase
        .from('contracts')
        .select(`
          *,
          property:properties(*, owner:profiles!owner_id(first_name, last_name)),
          tenant:profiles!tenant_id(first_name, last_name, email)
        `)
        .eq('tenant_id', filters.tenantId);

      if (contractsError) throw contractsError;

      // Role-based access control
      if (userContext.role === 'owner') {
        const accessibleContracts = contracts?.filter(c => 
          c.property?.owner_id === userContext.userId
        ) || [];
        
        if (accessibleContracts.length === 0) {
          return { success: false, error: 'Access denied: Tenant not associated with your properties' };
        }
      }

      // Get payment history
      const { data: payments } = await supabase
        .from('vouchers')
        .select(`
          *,
          property:properties(title)
        `)
        .eq('tenant_id', filters.tenantId)
        .eq('voucher_type', 'receipt');

      const totalPaid = payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
      const lastPayment = payments?.[0]?.created_at;

      return {
        success: true,
        data: {
          tenant: contracts?.[0]?.tenant,
          contracts: contracts || [],
          payments: payments || [],
          summary: { totalPaid, lastPayment },
          lastGenerated: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get tenant statement' };
    }
  },

  // Enhanced Owner Financial Report
  async getOwnerFinancialReport(userContext: UserContext, filters: ReportFilters): Promise<ApiResponse<any>> {
    try {
      if (!filters.ownerId) {
        return { success: false, error: 'Owner ID is required' };
      }

      // Role-based access control
      if (userContext.role === 'owner' && filters.ownerId !== userContext.userId) {
        return { success: false, error: 'Access denied: Can only view your own financial report' };
      }

      // Get owner's properties and financial data
      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select(`
          *,
          contracts(*),
          vouchers(*)
        `)
        .eq('owner_id', filters.ownerId);

      if (propertiesError) throw propertiesError;

      const totalRevenue = properties?.reduce((sum, p) => {
        const propertyRevenue = p.vouchers?.filter(v => v.voucher_type === 'receipt' && v.status === 'posted')
          .reduce((pSum, v) => pSum + (v.amount || 0), 0) || 0;
        return sum + propertyRevenue;
      }, 0) || 0;

      const totalExpenses = properties?.reduce((sum, p) => {
        const propertyExpenses = p.vouchers?.filter(v => v.voucher_type === 'payment' && v.status === 'posted')
          .reduce((pSum, v) => pSum + (v.amount || 0), 0) || 0;
        return sum + propertyExpenses;
      }, 0) || 0;

      return {
        success: true,
        data: {
          ownerId: filters.ownerId,
          properties: properties || [],
          summary: {
            totalProperties: properties?.length || 0,
            totalRevenue,
            totalExpenses,
            netIncome: totalRevenue - totalExpenses
          },
          lastGenerated: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get owner financial report' };
    }
  },

  // Enhanced Late Payments Report
  async getPaymentsAndLateTenantsReport(userContext: UserContext, filters: ReportFilters = {}): Promise<ApiResponse<any>> {
    try {
      let query = supabase
        .from('contracts')
        .select(`
          *,
          property:properties(*, owner_id),
          tenant:profiles!tenant_id(first_name, last_name, email),
          vouchers:vouchers!property_id(*)
        `)
        .eq('status', 'active');

      // Apply role-based filtering
      if (userContext.role === 'owner') {
        if (userContext.ownedPropertyIds) {
          query = query.in('property_id', userContext.ownedPropertyIds);
        } else {
          return { success: true, data: { latePayments: [], summary: { totalLate: 0, totalOverdue: 0 } } };
        }
      }

      if (filters.ownerId && userContext.role === 'manager') {
        const { data: ownerProperties } = await supabase
          .from('properties')
          .select('id')
          .eq('owner_id', filters.ownerId);
        
        if (ownerProperties) {
          query = query.in('property_id', ownerProperties.map(p => p.id));
        }
      }

      const { data: contracts, error } = await query;

      if (error) throw error;

      // Calculate payment status for each contract
      const paymentStatus = contracts?.map(contract => {
        const lastPayment = contract.vouchers
          ?.filter(v => v.voucher_type === 'receipt' && v.status === 'posted')
          ?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())?.[0];

        const daysSinceLastPayment = lastPayment 
          ? Math.floor((new Date().getTime() - new Date(lastPayment.created_at).getTime()) / (1000 * 60 * 60 * 24))
          : null;

        return {
          ...contract,
          lastPayment,
          daysSinceLastPayment,
          status: !lastPayment ? 'no_payments' : 
                  daysSinceLastPayment > 60 ? 'overdue' :
                  daysSinceLastPayment > 30 ? 'late' : 'current'
        };
      }) || [];

      const latePayments = paymentStatus.filter(p => p.status === 'late' || p.status === 'overdue');

      return {
        success: true,
        data: {
          latePayments,
          summary: {
            totalLate: latePayments.filter(p => p.status === 'late').length,
            totalOverdue: latePayments.filter(p => p.status === 'overdue').length
          },
          lastGenerated: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get payments and late tenants report' };
    }
  },

  // ... existing methods remain the same for backward compatibility ...
};

// Utility Payments API - For managing electrical and other utility payments
export const utilityPaymentsApi = {
  // Get all utility payments with filtering
  async getAll(filters?: {
    property_id?: string;
    utility_type?: string;
    payment_status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<any[]>> {
    return handleApiCall(async () => {
      let query = supabase
        .from('utility_payments')
        .select(`
          *,
          property:properties(id, title, address, city),
          uploaded_by_profile:profiles!utility_payments_uploaded_by_fkey(first_name, last_name)
        `)
        .order('reading_date', { ascending: false });

      if (filters?.property_id) query = query.eq('property_id', filters.property_id);
      if (filters?.utility_type) query = query.eq('utility_type', filters.utility_type);
      if (filters?.payment_status) query = query.eq('payment_status', filters.payment_status);
      if (filters?.start_date) query = query.gte('reading_date', filters.start_date);
      if (filters?.end_date) query = query.lte('reading_date', filters.end_date);

      return await query;
    });
  },

  // Upload new electrical payment data
  async uploadElectricalPayment(paymentData: {
    property_id: string;
    meter_number?: string;
    previous_reading: number;
    current_reading: number;
    rate_per_unit?: number;
    reading_date: string;
    due_date?: string;
    payment_status?: 'pending' | 'paid' | 'overdue';
    payment_date?: string;
    payment_method?: string;
    payment_reference?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      const userContext = await getCurrentUserContext();
      
      if (!userContext || !['admin', 'manager'].includes(userContext.role)) {
        throw new Error('Only property managers and admins can upload electrical payments');
      }

      const insertData = {
        ...paymentData,
        utility_type: 'electricity',
        rate_per_unit: paymentData.rate_per_unit || 0.18, // Default SAR per kWh
        uploaded_by: userContext.userId
      };

      return supabase
        .from('utility_payments')
        .insert(insertData)
        .select(`
          *,
          property:properties(id, title, address)
        `)
        .single();
    });
  },

  // Bulk upload electrical payments (CSV import functionality)
  async bulkUploadElectricalPayments(payments: Array<{
    property_id: string;
    meter_number?: string;
    previous_reading: number;
    current_reading: number;
    reading_date: string;
    due_date?: string;
    notes?: string;
  }>): Promise<ApiResponse<any[]>> {
    return handleApiCall(async () => {
      const userContext = await getCurrentUserContext();
      
      if (!userContext || !['admin', 'manager'].includes(userContext.role)) {
        throw new Error('Only property managers and admins can bulk upload electrical payments');
      }

      const insertData = payments.map(payment => ({
        ...payment,
        utility_type: 'electricity',
        rate_per_unit: 0.18, // Default SAR per kWh
        uploaded_by: userContext.userId,
        payment_status: 'pending'
      }));

      return supabase
        .from('utility_payments')
        .insert(insertData)
        .select(`
          *,
          property:properties(id, title, address)
        `);
    });
  },

  // Update payment status (when payment is made)
  async updatePaymentStatus(paymentId: string, updates: {
    payment_status: 'pending' | 'paid' | 'overdue';
    payment_date?: string;
    payment_method?: string;
    payment_reference?: string;
    voucher_id?: string;
  }): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      return supabase
        .from('utility_payments')
        .update(updates)
        .eq('id', paymentId)
        .select()
        .single();
    });
  },

  // Get electrical payments by property
  async getByProperty(propertyId: string): Promise<ApiResponse<any[]>> {
    return handleApiCall(async () => {
      return supabase
        .from('utility_payments')
        .select(`
          *,
          property:properties(id, title, address)
        `)
        .eq('property_id', propertyId)
        .eq('utility_type', 'electricity')
        .order('reading_date', { ascending: false });
    });
  },

  // Get overdue payments
  async getOverduePayments(): Promise<ApiResponse<any[]>> {
    return handleApiCall(async () => {
      return supabase
        .from('utility_payments')
        .select(`
          *,
          property:properties(id, title, address, owner_id),
          property.profiles:properties!property_id(first_name, last_name, email, phone_number)
        `)
        .eq('payment_status', 'overdue')
        .order('due_date', { ascending: true });
    });
  },

  // Get utility payment statistics
  async getStatistics(): Promise<ApiResponse<{
    totalPayments: number;
    pendingPayments: number;
    paidPayments: number;
    overduePayments: number;
    totalAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    averageConsumption: number;
  }>> {
    return handleApiCall(async () => {
      const { data: payments } = await supabase
        .from('utility_payments')
        .select('payment_status, amount, consumption')
        .eq('utility_type', 'electricity');

      if (!payments) return { data: null, error: null };

      const stats = {
        totalPayments: payments.length,
        pendingPayments: payments.filter(p => p.payment_status === 'pending').length,
        paidPayments: payments.filter(p => p.payment_status === 'paid').length,
        overduePayments: payments.filter(p => p.payment_status === 'overdue').length,
        totalAmount: payments.reduce((sum, p) => sum + Number(p.amount || 0), 0),
        pendingAmount: payments.filter(p => p.payment_status === 'pending')
          .reduce((sum, p) => sum + Number(p.amount || 0), 0),
        overdueAmount: payments.filter(p => p.payment_status === 'overdue')
          .reduce((sum, p) => sum + Number(p.amount || 0), 0),
        averageConsumption: payments.length > 0 ? 
          payments.reduce((sum, p) => sum + Number(p.consumption || 0), 0) / payments.length : 0
      };

      return { data: stats, error: null };
    });
  },

  // Upload new water payment data
  async uploadWaterPayment(paymentData: {
    property_id: string;
    meter_number?: string;
    previous_reading: number;
    current_reading: number;
    rate_per_unit?: number;
    reading_date: string;
    due_date?: string;
    payment_status?: 'pending' | 'paid' | 'overdue';
    payment_date?: string;
    payment_method?: string;
    payment_reference?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    return handleApiCall(async () => {
      const userContext = await getCurrentUserContext();
      
      if (!userContext || !['admin', 'manager'].includes(userContext.role)) {
        throw new Error('Only property managers and admins can upload water payments');
      }

      const consumption = paymentData.current_reading - paymentData.previous_reading;
      const amount = consumption * (paymentData.rate_per_unit || 0.15); // Default SAR per m³

      const insertData = {
        ...paymentData,
        utility_type: 'water',
        consumption,
        amount,
        rate_per_unit: paymentData.rate_per_unit || 0.15, // Default SAR per m³
        uploaded_by: userContext.userId
      };

      return supabase
        .from('utility_payments')
        .insert(insertData)
        .select(`
          *,
          property:properties(id, title, address)
        `)
        .single();
    });
  },

  // Get water payments by property
  async getWaterByProperty(propertyId: string): Promise<ApiResponse<any[]>> {
    return handleApiCall(async () => {
      return supabase
        .from('utility_payments')
        .select(`
          *,
          property:properties(id, title, address)
        `)
        .eq('property_id', propertyId)
        .eq('utility_type', 'water')
        .order('reading_date', { ascending: false });
    });
  }
};

// Export all APIs
export default {
  properties: propertiesApi,
  profiles: profilesApi,
  contracts: contractsApi,
  maintenance: maintenanceApi,
  accounts: accountsApi,
  costCenters: costCentersApi,
  vouchers: vouchersApi,
  invoices: invoicesApi,
  issues: issuesApi,
  documents: documentsApi,
  clients: clientsApi,
  reservations: reservationsApi,
  reports: reportsApi,
  bids: bidsApi,
  userApprovals: userApprovalsApi,
  tenant: tenantApi,
  bidding: biddingApi,
  ownerMaintenance: ownerMaintenanceApi,
  ownerProperty: ownerPropertyApi,
  approvals: approvalsApi,
  buyer: buyerApi,
  notifications: notificationsApi,
  utilityPayments: utilityPaymentsApi,
  enhancedReports: enhancedReportsApi,
}; 

// Add specialized methods for report filters (bypass security for dropdown options)
export const reportFiltersApi = {
  // Get all tenants for report filtering (bypasses security)
  async getAllTenantsForReports(): Promise<ApiResponse<any[]>> {
    return handleApiCall(() => 
      supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          status
        `)
        .eq('role', 'tenant')
        .eq('status', 'active')
        .order('first_name')
    );
  },

  // Get all owners for report filtering (bypasses security)
  async getAllOwnersForReports(): Promise<ApiResponse<any[]>> {
    return handleApiCall(() => 
      supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          status
        `)
        .eq('role', 'owner')
        .eq('status', 'active')
        .order('first_name')
    );
  },

  // Get all properties for report filtering (bypasses security)
  async getAllPropertiesForReports(): Promise<ApiResponse<any[]>> {
    return handleApiCall(() => 
      supabase
        .from('properties')
        .select(`
          id,
          title,
          address,
          city,
          status,
          property_type
        `)
        .neq('status', 'deleted')
        .order('title')
    );
  }
}; 

// Sub-Property Management API
export const subPropertiesApi = {
  async getByParentProperty(parentPropertyId: string): Promise<ApiResponse<any[]>> {
    try {
      const access = await supabase.rpc('subproperty_private_access', { property_uuid: parentPropertyId });
      if (access.error) throw access.error;
      if (access.data !== true) throw new Error('SUBPROPERTY_ACCESS_DENIED');
      const { data, error } = await supabase.from('properties').select(`
        *, contracts(id,contract_number,payment_frequency,contract_duration_years,base_price,rent_amount,status,start_date,end_date,
          tenant:profiles!contracts_tenant_id_fkey(id,first_name,last_name,phone_number,email)),
        property_meters(id,meter_number,meter_type,current_reading),
        property_contacts(id,contact_type,phone_number,is_primary,label)
      `).eq('parent_property_id', parentPropertyId).eq('is_sub_property', true).order('created_at', { ascending: false });
      if (error) throw error;
      if (!Array.isArray(data)) throw new Error('SUBPROPERTY_INVALID_RESPONSE');
      return { data, error: null };
    } catch (error) { return { data: null, error: error as Error }; }
  },

  async create(subPropertyData: {
    request_id: string; parent_property_id: string; title: string; property_type: string; area_sqm: number;
    bedrooms?: number; bathrooms?: number; floor_number?: number; unit_number?: string; unit_label?: string;
    base_price: number; rent_amount: number; payment_frequency: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
    contract_duration_years: number; contract_number: string; contract_pdf_url: string;
    tenant_contact_numbers: string[]; meter_numbers?: string[]; description?: string; amenities?: string[];
    is_furnished?: boolean; parking_spaces?: number; service_charge?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const { request_id, parent_property_id, ...payload } = subPropertyData;
      if (!request_id || !parent_property_id) throw new Error('SUBPROPERTY_INVALID_INPUT');
      const { data, error } = await supabase.rpc('create_subproperty_atomic', { request_id, parent_id: parent_property_id, payload });
      if (error) throw error;
      if (data?.property?.id !== request_id || data?.contract?.id !== request_id ||
          data.property.parent_property_id !== parent_property_id || data.contract.property_id !== request_id) {
        throw new Error('SUBPROPERTY_INVALID_RESPONSE');
      }
      return { data, error: null };
    } catch (error) { return { data: null, error: error as Error }; }
  },

  async update(id: string, parentPropertyId: string, changes: Partial<{
    title: string; area_sqm: number; bedrooms: number; bathrooms: number; floor_number: number;
    unit_number: string; unit_label: string; description: string;
  }>): Promise<ApiResponse<any>> {
    try {
      if (!id || !parentPropertyId) throw new Error('SUBPROPERTY_INVALID_INPUT');
      const { data, error } = await supabase.rpc('update_subproperty_atomic', { child_id: id, parent_id: parentPropertyId, changes });
      if (error) throw error;
      if (data?.id !== id || data.parent_property_id !== parentPropertyId || data.is_sub_property !== true) throw new Error('SUBPROPERTY_INVALID_RESPONSE');
      return { data, error: null };
    } catch (error) { return { data: null, error: error as Error }; }
  },

  async delete(id: string, parentPropertyId: string): Promise<ApiResponse<boolean>> {
    try {
      if (!id || !parentPropertyId) throw new Error('SUBPROPERTY_INVALID_INPUT');
      const { data, error } = await supabase.rpc('delete_subproperty_atomic', { child_id: id, parent_id: parentPropertyId });
      if (error) throw error;
      if (data !== true) throw new Error('SUBPROPERTY_INVALID_RESPONSE');
      return { data: true, error: null };
    } catch (error) { return { data: false, error: error as Error }; }
  },

  async getById(id: string): Promise<ApiResponse<any>> {
    try {
      const access = await supabase.rpc('subproperty_private_access', { property_uuid: id });
      if (access.error) throw access.error;
      if (access.data !== true) throw new Error('SUBPROPERTY_ACCESS_DENIED');
      const { data, error } = await supabase.from('properties').select(`
        *, contracts(id,contract_number,payment_frequency,contract_duration_years,base_price,rent_amount,start_date,end_date,status,
          tenant:profiles!contracts_tenant_id_fkey(id,first_name,last_name,phone_number,email)),
        property_meters(id,meter_number,meter_type,current_reading,last_reading_date),
        property_contacts(id,contact_type,phone_number,is_primary,label)
      `).eq('id', id).eq('is_sub_property', true).single();
      if (error) throw error;
      if (data?.id !== id) throw new Error('SUBPROPERTY_INVALID_RESPONSE');
      return { data, error: null };
    } catch (error) { return { data: null, error: error as Error }; }
  }
};
